import { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback, memo, Fragment, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  Plus, Trash2, Folder as FolderIcon, FolderPlus,
  Upload, FileSpreadsheet, X, AlertTriangle,
  Sliders, Wrench, ChevronDown, ChevronRight, Lightbulb,
  Maximize2, Minimize2, FileDown, FilePlus2,
} from 'lucide-react'
import {
  type StructureData, type Folder, type Rights, type Permission, type Group,
  type Drive, type Share, type PermissionPreset, type Responsible,
  RIGHTS_OPTIONS, PERMISSION_PRESETS, rightsToPreset, presetByValue,
  fullGroupName,
} from '../types.ts'
import { ResponsiblePicker } from './matrix/ResponsiblePicker.tsx'
import { getInheritedResponsibles } from './matrix/matrix-helpers.ts'
import { useMatrixCaps } from './matrix/MatrixCapabilities.tsx'
import {
  exportMatrixToExcel, exportMatrixTemplate, parseMatrixFile, applyImport,
  type ParsedImport, type ImportSummary,
} from '../matrix-excel.ts'
import {
  buildPermissionSuggestions,
  applySuggestions,
  type PermissionSuggestion,
} from './matrix/matrix-suggestions.ts'
import {
  Button,
  Checkbox,
  ConfirmDialog,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownLabel,
  DropdownSeparator,
  DropdownTrigger,
  FilterChip,
  IconButton,
  Modal,
} from '@/components/ui'
import { cn } from '@/lib/utils.ts'

interface Props {
  data: StructureData
  update: (patch: Partial<StructureData>) => void
}

const RIGHTS_BADGE: Record<string, string> = {
  FullControl:  'bg-orange-400 border-orange-600',
  Modify:       'bg-green-300 border-green-500',
  ReadExecute:  'bg-yellow-300 border-yellow-500',
  Read:         'bg-yellow-300 border-yellow-500',
  Write:        'bg-amber-200 border-amber-400',
  ListFolder:   'bg-yellow-300 border-yellow-500',
  None:         'bg-red-200 border-red-400',
  Revoke:       'bg-gray-200 border-gray-400',
}

const PRESET_BADGE: Record<string, string> = {
  orange:  'bg-orange-400 border-orange-600',
  green:   'bg-green-300 border-green-500',
  mixed:   'bg-amber-200 border-amber-400',
  yellow:  'bg-yellow-300 border-yellow-500',
  red:     'bg-red-200 border-red-400',
  white:   'bg-white border-gray-300',
  gray:    'bg-gray-200 border-gray-400',
}

/**
 * Color coding based on the preset category — maps NTFS rights
 * to visual classes:
 *   Orange  = Full control (FullControl) — warning color, "admins only"
 *   Green   = Modify — standard write rights
 *   Amber   = Drop box (Write without Read) — distinct mixed color
 *   Yellow  = Read-only (Read, ReadExecute, ListFolder)
 *   Red     = DENY (None) — explicit denial, overrides other groups
 *   Gray    = Revoked (Revoke) — no entry in ACL, but no DENY
 *   White   = no permission set (inherits from parent)
 *
 * When `inherited === true`, a paler variant is shown so directly
 * set rights stand out visually.
 */
function rightsBgClass(rights: Rights | null, inherited: boolean): string {
  if (!rights) return 'bg-white'
  const preset = rightsToPreset(rights)
  const cat = presetByValue(preset).category
  if (cat === 'white') return 'bg-white'
  if (inherited) {
    if (cat === 'orange') return 'bg-orange-50 text-orange-700'
    if (cat === 'green') return 'bg-green-50 text-green-700'
    if (cat === 'mixed') return 'bg-amber-50 text-amber-700'
    if (cat === 'yellow') return 'bg-yellow-50 text-yellow-800'
    if (cat === 'gray') return 'bg-gray-50 text-gray-500'
    return 'bg-red-50 text-red-600'
  }
  if (cat === 'orange') return 'bg-orange-400 text-orange-950'
  if (cat === 'green') return 'bg-green-300 text-green-900'
  if (cat === 'mixed') return 'bg-amber-200 text-amber-900'
  if (cat === 'yellow') return 'bg-yellow-300 text-yellow-900'
  if (cat === 'gray') return 'bg-gray-200 text-gray-700'
  return 'bg-red-200 text-red-800'
}

/**
 * Shows a collapsible warning about all (folder, group) pairs where
 * the access chain is broken: the folder has rights, but a parent folder
 * (or the share root) blocks access via DENY/Revoke/missing permission.
 * In Windows/NTFS such rights are effectively useless.
 */
function PlausibilityBanner({
  issues,
  folders,
  allFolders,
  shares,
  permissions,
  groups,
  domainPrefix,
  onApplyFixes,
}: {
  issues: Map<string, Map<string, { ancestorName: string; issue: string }>>
  folders: Folder[]
  allFolders: Folder[]
  shares: Share[]
  permissions: Permission[]
  groups: Group[]
  domainPrefix?: string
  onApplyFixes: (suggestions: PermissionSuggestion[]) => void
}) {
  const [open, setOpen] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const suggestions = useMemo(
    () =>
      buildPermissionSuggestions({
        issues,
        allFolders,
        allPermissions: permissions,
        shares,
        groups,
        groupLabel: (g) => g.teamName || fullGroupName(domainPrefix, g.type, g.name),
      }),
    [issues, allFolders, permissions, shares, groups, domainPrefix],
  )

  // When the modal opens, pre-select all suggestions — the user can
  // then deselect individual items they don't want to apply.
  useEffect(() => {
    if (showSuggestions) setSelectedIds(new Set(suggestions.map((s) => s.id)))
  }, [showSuggestions, suggestions])

  let total = 0
  for (const m of issues.values()) total += m.size
  if (total === 0) return null

  const groupById = new Map(groups.map((g) => [g.id, g] as const))
  const folderById = new Map(folders.map((f) => [f.id, f] as const))
  const folderPath = (folderId: string): string => {
    const names: string[] = []
    let cur: Folder | undefined = folderById.get(folderId)
    while (cur) {
      names.unshift(cur.name)
      cur = cur.parent ? folderById.get(cur.parent) : undefined
    }
    return names.join('\\')
  }
  const affectedFolders = issues.size

  const selected = suggestions.filter((s) => selectedIds.has(s.id))
  function toggle(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  function toggleAll() {
    setSelectedIds((prev) =>
      prev.size === suggestions.length ? new Set() : new Set(suggestions.map((s) => s.id)),
    )
  }
  function applySelected() {
    if (selected.length === 0) return
    onApplyFixes(selected)
    setShowSuggestions(false)
  }

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-small text-amber-900">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600" />
        <div className="flex-1 min-w-0">
          <p className="font-medium">
            {total} Plausibilitäts-Warnung{total === 1 ? '' : 'en'}: Zugriffspfad gebrochen
            {affectedFolders > 1 ? ` (${affectedFolders} Ordner betroffen)` : ''}
          </p>
          <p className="text-caption mt-0.5 text-amber-800">
            Eine Gruppe hat Rechte auf einem Unterordner, aber der übergeordnete Ordner
            (oder der Share-Root) verweigert den Zugriff per DENY, "Entziehen" oder fehlender
            Berechtigung. Windows lässt die Gruppe dann gar nicht erst in den Pfad hinein —
            die Rechte am Unterordner sind wirkungslos.
          </p>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <Button
              variant="link"
              size="xs"
              onClick={() => setOpen((x) => !x)}
              className="text-amber-700 hover:text-amber-900 underline"
            >
              {open ? 'Details ausblenden' : 'Details anzeigen'}
            </Button>
            {suggestions.length > 0 && (
              <Button
                variant="secondary"
                size="xs"
                leadingIcon={<Lightbulb className="w-3.5 h-3.5" />}
                onClick={() => setShowSuggestions(true)}
                className="border-amber-400 text-amber-800 hover:bg-amber-100"
                title="Minimale Rechte vorschlagen, um die Zugriffspfade zu reparieren"
              >
                {suggestions.length} Vorschl{suggestions.length === 1 ? 'ag' : 'äge'} zum Beheben
              </Button>
            )}
          </div>
          {open && (
            <ul className="mt-2 space-y-1 text-caption bg-white border border-amber-200 rounded p-2 max-h-60 overflow-y-auto">
              {[...issues.entries()].flatMap(([folderId, perGroup]) =>
                [...perGroup.entries()].map(([groupId, info]) => {
                  const g = groupById.get(groupId)
                  const groupLabel = g ? (g.teamName || fullGroupName(domainPrefix, g.type, g.name)) : groupId
                  return (
                    <li key={`${folderId}:${groupId}`} className="font-mono text-[11px]">
                      <span className="text-gray-800">{folderPath(folderId)}</span>
                      <span className="text-gray-400"> × </span>
                      <span className="text-amber-700">{groupLabel}</span>
                      <span className="text-gray-400"> → blockiert durch </span>
                      <span className="text-red-700">{info.ancestorName}</span>
                      <span className="text-gray-500"> ({info.issue})</span>
                    </li>
                  )
                }),
              )}
            </ul>
          )}
        </div>
      </div>

      <Modal
        open={showSuggestions}
        onOpenChange={setShowSuggestions}
        size="xl"
        title={
          <span className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            Automatische Vorschläge mit den geringsten Rechten
          </span>
        }
        description={
          <span className="text-caption text-gray-600">
            Für jeden Zielordner mit Zugriff wird die komplette Kette bis zum Share-Root
            mit dem kleinstmöglichen Traversal-Recht ausgestattet: <b>ListFolder</b> mit
            Scope <b>„Nur dieser Ordner"</b>. Dadurch kann die Gruppe nur den
            benötigten Pfad durchwandern — Geschwister-Ordner erben nichts. Bestehende
            DENY/Revoke-Einträge auf diesen Knoten werden ersetzt, alles andere bleibt
            unangetastet.
          </span>
        }
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setShowSuggestions(false)}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={selected.length === 0}
              onClick={applySelected}
            >
              {selected.length === suggestions.length
                ? `Alle ${suggestions.length} übernehmen`
                : `${selected.length} übernehmen`}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {suggestions.length === 0 ? (
            <p className="text-small text-gray-500 italic">Keine automatisch behebbaren Fälle gefunden.</p>
          ) : (
            <>
              <div className="flex items-center justify-between text-caption text-gray-600 border-b border-gray-200 pb-2">
                <Checkbox
                  checked={selectedIds.size === suggestions.length}
                  indeterminate={selectedIds.size > 0 && selectedIds.size < suggestions.length}
                  onChange={toggleAll}
                  label={
                    selectedIds.size === suggestions.length ? 'Alle abwählen' : 'Alle auswählen'
                  }
                />
                <span>
                  {total} Warnung{total === 1 ? '' : 'en'} →{' '}
                  <b>{suggestions.length}</b> Kettenknoten brauchen ein Traversal-Recht
                </span>
              </div>
              <div className="max-h-[55vh] overflow-y-auto border border-gray-200 rounded">
                <table className="w-full text-caption">
                  <thead className="bg-gray-50 text-gray-600 sticky top-0">
                    <tr>
                      <th className="p-2 w-8" />
                      <th className="p-2 text-left font-semibold">Ziel</th>
                      <th className="p-2 text-left font-semibold">Gruppe</th>
                      <th className="p-2 text-left font-semibold">Aktuell</th>
                      <th className="p-2 text-left font-semibold">Vorschlag</th>
                      <th className="p-2 text-right font-semibold">Behebt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggestions.map((s) => {
                      const checked = selectedIds.has(s.id)
                      return (
                        <tr
                          key={s.id}
                          className={cn(
                            'border-t border-gray-100 hover:bg-amber-50/40 transition',
                            checked ? 'bg-white' : 'bg-gray-50 text-gray-500',
                          )}
                        >
                          <td className="p-2 align-top">
                            <Checkbox checked={checked} onChange={() => toggle(s.id)} />
                          </td>
                          <td className="p-2 align-top">
                            <div className="font-mono text-[11px] text-gray-900">{s.targetLabel}</div>
                            <div className="text-[10px] text-gray-500 uppercase tracking-wide">
                              {s.targetKind}
                            </div>
                          </td>
                          <td className="p-2 align-top text-amber-800">{s.groupLabel}</td>
                          <td className="p-2 align-top">
                            <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono border bg-red-50 text-red-700 border-red-200">
                              {s.currentIssue}
                            </span>
                          </td>
                          <td className="p-2 align-top">
                            <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-bold border bg-yellow-100 text-yellow-900 border-yellow-300">
                              L · Inhalt auflisten
                            </span>
                            <div className="text-[10px] text-gray-500 mt-0.5 italic">
                              nur dieser Ordner — vererbt nicht
                            </div>
                          </td>
                          <td className="p-2 align-top text-right text-gray-600">
                            {s.affectedFolderIds.length}{' '}
                            <span className="text-gray-400">
                              Ordner
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-500">
                Hinweis: Jeder Vorschlag setzt <b>ListFolder · Nur dieser Ordner</b>. Die
                Spalte <i>Behebt</i> zählt, für wie viele Zielordner dieser Knoten im
                Zugriffspfad liegt. Nach dem Anwenden wird die Warnungsliste automatisch
                neu berechnet.
              </p>
            </>
          )}
        </div>
      </Modal>
    </div>
  )
}

// Sticky-Column-Styling: Die ersten 6 Spalten (LW, Benennung, Server, Share,
// Ordner, Verantwortlich) bleiben beim horizontalen Scrollen festgepinnt — nur
// die Abteilungs-Spalten scrollen. Die Breiten MUeSSEN exakt zu den Header-
// Widths passen, sonst stimmen die left-Offsets nicht.
const STICKY = {
  lw: 'sticky left-0',
  benennung: 'sticky left-[36px]',
  server: 'sticky left-[108px]',
  share: 'sticky left-[178px]',
  ordner: 'sticky left-[248px]',
  // Letzte Sticky-Spalte bekommt einen Schatten, damit die Grenze zur
  // scrollenden Abteilungs-Flaeche sichtbar ist.
  verantwortlich: 'sticky left-[508px] shadow-[2px_0_0_rgb(209,213,219)]',
} as const

// Discriminated Union: Ziel einer Permission ist entweder ein Folder ODER ein
// Share-Root — nie beides gleichzeitig. Auf Modul-Ebene definiert, damit der
// memoisierte <PermissionCell> unten darauf zugreifen kann.
type PermTarget =
  | { folderId: string; shareId?: undefined }
  | { shareId: string; folderId?: undefined }

// Eindeutiger Key fuer permIndex-Lookups: unterscheidet zwischen Folder- und
// Share-Target, damit gleiche IDs in verschiedenen Namespaces nicht kollidieren.
function permTargetKey(target: PermTarget, groupId: string): string {
  return target.folderId !== undefined
    ? `F:${target.folderId}|${groupId}`
    : `S:${target.shareId}|${groupId}`
}

// Eine einzelne Permission-Zelle in der Matrix. Als eigenstaendige, memoisierte
// Komponente gerendert, damit bei einem Klick auf Zelle X die anderen
// ~1000 Zellen NICHT neu rendern — bei der Zugriffsmatrix ist das der mit
// Abstand wichtigste Performance-Hebel.
type PermissionCellProps = {
  target: PermTarget
  groupId: string
  direct: Rights | null
  inherited: Rights | null
  issue: { ancestorName: string; issue: string } | null
  advancedMode: boolean
  onCycle: (target: PermTarget, groupId: string, backward: boolean) => void
  onOpenMenu: (e: React.MouseEvent, target: PermTarget, groupId: string) => void
}

const PermissionCell = memo(function PermissionCell({
  target,
  groupId,
  direct,
  inherited,
  issue,
  advancedMode,
  onCycle,
  onOpenMenu,
}: PermissionCellProps) {
  const effective = direct ?? inherited
  const effectiveOpt = effective ? RIGHTS_OPTIONS.find((o) => o.value === effective) : null
  const effectivePreset = effective ? presetByValue(rightsToPreset(effective)) : null
  const bg = rightsBgClass(effective, !direct && !!inherited)
  const displayShort = advancedMode
    ? (effectiveOpt?.short ?? '—')
    : (effectivePreset?.short ?? '—')
  const displayLabel = advancedMode
    ? (effectiveOpt?.label ?? 'Keine Berechtigung')
    : (effectivePreset?.label ?? 'Keine Berechtigung')
  const baseTitle = direct
    ? `Direkt: ${displayLabel} (Klick: nächster Modus, Shift+Klick: vorheriger, Rechtsklick: Auswahl)`
    : inherited
      ? `Geerbt: ${displayLabel} (Klick setzt direkten Modus, Rechtsklick: Auswahl)`
      : 'Keine Berechtigung (Klick setzt Modus, Rechtsklick: Auswahl)'
  const cellTitle = issue
    ? `⚠ Zugriff nicht möglich: Elternordner "${issue.ancestorName}" hat ${issue.issue}. ` +
      `Damit dieser Ordner erreichbar ist, muss die Gruppe auf allen übergeordneten Ordnern ` +
      `mindestens Lese-/Durchwander-Rechte haben.\n\n${baseTitle}`
    : baseTitle
  return (
    <td className={cn('relative border border-gray-300 p-0 text-center', bg)}>
      <button
        type="button"
        onMouseDown={(e) => {
          if (e.button === 0) {
            onCycle(target, groupId, e.shiftKey)
          } else if (e.button === 2) {
            e.preventDefault()
            onOpenMenu(e, target, groupId)
          }
        }}
        onContextMenu={(e) => e.preventDefault()}
        className={cn(
          'w-full h-full text-[10px] font-mono px-1 py-1 border-0 bg-transparent select-none cursor-pointer hover:ring-1 hover:ring-inset hover:ring-brand-500 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-brand-500',
          inherited && !direct ? 'italic opacity-80' : 'font-semibold',
        )}
        title={cellTitle}
      >
        {displayShort}
      </button>
      {issue && (
        <span
          aria-hidden
          className="pointer-events-none absolute top-0 right-0 leading-none"
          title={cellTitle}
        >
          <AlertTriangle className="w-2.5 h-2.5 text-red-600 drop-shadow-[0_0_1px_white]" />
        </span>
      )}
    </td>
  )
}, (prev, next) =>
  prev.direct === next.direct &&
  prev.inherited === next.inherited &&
  prev.advancedMode === next.advancedMode &&
  prev.groupId === next.groupId &&
  prev.target.folderId === next.target.folderId &&
  prev.target.shareId === next.target.shareId &&
  prev.issue?.ancestorName === next.issue?.ancestorName &&
  prev.issue?.issue === next.issue?.issue &&
  prev.onCycle === next.onCycle &&
  prev.onOpenMenu === next.onOpenMenu,
)

export function MatrixTab({ data, update }: Props) {
  const caps = useMatrixCaps()
  const servers = data.servers
  // Multi-Server-Filter: Default = alle Server aktiv. Wichtig u.a. damit
  // doppelte Laufwerksbuchstaben ueber Server hinweg erkannt werden koennen.
  // Neu hinzugefuegte Server werden automatisch aktiviert.
  const [selectedServerIds, setSelectedServerIds] = useState<string[]>(() => servers.map((s) => s.id))
  useEffect(() => {
    setSelectedServerIds((prev) => {
      const known = new Set(servers.map((s) => s.id))
      const kept = prev.filter((id) => known.has(id))
      const missing = servers.map((s) => s.id).filter((id) => !prev.includes(id))
      if (kept.length === prev.length && missing.length === 0) return prev
      return [...kept, ...missing]
    })
  }, [servers])
  const selectedServerSet = useMemo(() => new Set(selectedServerIds), [selectedServerIds])
  function toggleServer(id: string) {
    setSelectedServerIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }
  function selectAllServers() {
    setSelectedServerIds(servers.map((s) => s.id))
  }
  function clearServers() {
    setSelectedServerIds([])
  }
  const allSelected = servers.length > 0 && selectedServerIds.length === servers.length
  const noneSelected = selectedServerIds.length === 0

  // Excel Import/Export State
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [showImportModal, setShowImportModal] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importPreview, setImportPreview] = useState<
    { parsed: ParsedImport; summary: ImportSummary; patch: Partial<StructureData> } | null
  >(null)
  const [importError, setImportError] = useState<string | null>(null)
  const [showImportDebug, setShowImportDebug] = useState(true)
  const [importReplaceExisting, setImportReplaceExisting] = useState(false)

  // Drag & Drop: Datei direkt auf die Matrix ziehen
  const [isDragOver, setIsDragOver] = useState(false)
  const dragCounterRef = useRef(0)

  /** Legt eine neue Abteilungs-Gruppe per Prompt an. */
  function addAbteilung() {
    const name = window.prompt('Name der neuen Abteilung (z.B. Buchhaltung):')
    if (!name) return
    const trimmed = name.trim()
    if (!trimmed) return
    const g: Group = { id: crypto.randomUUID(), type: 'AB', name: trimmed }
    update({ groups: [...data.groups, g] })
  }

  // Draft-Rows: mindestens 10 leere Zeilen am Ende der Tabelle fuer
  // Direkt-Eingabe. Jede Zeile ist eine freie Sheet-Zeile, die beim
  // Commit (Enter/Blur auf einer Zelle) in Drive/Share/Folder zerlegt
  // wird.
  type DraftRow = {
    driveLetter: string
    driveLabel: string
    serverName: string
    shareName: string
    levelNames: string[]
    responsibleEmployees: Responsible[]
  }
  const emptyDraft = (): DraftRow => ({
    driveLetter: '',
    driveLabel: '',
    serverName: '',
    shareName: '',
    levelNames: [],
    responsibleEmployees: [],
  })
  const [draftRows, setDraftRows] = useState<DraftRow[]>(() => Array.from({ length: 10 }, emptyDraft))
  // Fehler-Meldung fuer die Draft-Zeilen (z.B. „Server-Name fehlt bei mehreren
  // aktiven Servern"). Wird oberhalb der Tabelle angezeigt und bei jedem
  // neuen Commit-Versuch zurueckgesetzt.
  const [draftError, setDraftError] = useState<string | null>(null)

  // Sub-Ordner-Drafts: Key = Parent-Folder-ID oder `share:<shareId>` fuer
  // Top-Level-Folder im Share-Root. Wird direkt UNTER der Parent-Zeile
  // gerendert und erzeugt beim Commit einen Child-Folder mit geerbtem
  // shareId/driveId vom Parent.
  const [subDrafts, setSubDrafts] = useState<Record<string, string>>({})

  // Vollbild-Modus: Die Matrix legt sich ueber das komplette Browser-Fenster
  // (fixed inset-0). Praktisch beim Bearbeiten breiter Abteilungs-Matrizen.
  // ESC schliesst, Body-Scroll wird waehrenddessen gesperrt.
  const [isFullscreen, setIsFullscreen] = useState(false)
  useEffect(() => {
    if (!isFullscreen) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsFullscreen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
    }
  }, [isFullscreen])

  // Erweitert-Modus: zeigt die vollen 7 NTFS-Stufen statt der 6 Presets.
  // Persistiert in localStorage, damit Power-User nicht jedes Mal neu umschalten muessen.
  const [advancedMode, setAdvancedMode] = useState<boolean>(() => {
    try {
       
      return localStorage.getItem('matrix-advanced-mode') === '1'
    } catch {
      return false
    }
  })
  useEffect(() => {
    try {
       
      localStorage.setItem('matrix-advanced-mode', advancedMode ? '1' : '0')
    } catch {
      /* SSR or storage disabled — ignore */
    }
  }, [advancedMode])

  // Bulk-Apply-Menue pro Gruppen-Spalte. null = zu. Position in
  // Viewport-Koordinaten (fixed) — sonst clippt `overflow-x-auto` auf dem
  // Tabellen-Container den oberen Teil des Popups weg.
  const [bulkMenu, setBulkMenu] = useState<
    { groupId: string; x: number; y: number } | null
  >(null)
   
  const bulkMenuRef = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    if (!bulkMenu || !bulkMenuRef.current) return
    const rect = bulkMenuRef.current.getBoundingClientRect()
    const pad = 8
     
    const vw = window.innerWidth
     
    const vh = window.innerHeight
    // Popup wird per `transform: translate(-100%,-100%)` so verschoben, dass
    // seine untere rechte Ecke bei (bulkMenu.x, bulkMenu.y) liegt. Daher alle
    // vier Kanten pruefen und den Anker entsprechend verschieben.
    let x = bulkMenu.x
    let y = bulkMenu.y
    if (rect.right > vw - pad) x -= rect.right - (vw - pad)
    if (rect.left < pad) x += pad - rect.left
    if (rect.bottom > vh - pad) y -= rect.bottom - (vh - pad)
    if (rect.top < pad) y += pad - rect.top
    if (x !== bulkMenu.x || y !== bulkMenu.y) {
      setBulkMenu((prev) => (prev ? { ...prev, x, y } : prev))
    }
  }, [bulkMenu])

  useEffect(() => {
    if (!bulkMenu) return
     
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setBulkMenu(null) }
    const onDocClick = () => setBulkMenu(null)
     
    document.addEventListener('keydown', onKey)
     
    document.addEventListener('click', onDocClick)
    return () => {
       
      document.removeEventListener('keydown', onKey)
       
      document.removeEventListener('click', onDocClick)
    }
  }, [bulkMenu])

  // Zentraler Bestaetigungs-Dialog fuer destruktive Aktionen (Share/Folder
  // loeschen). null = geschlossen. `onConfirm` fuehrt die eigentliche Aktion aus.
  const [confirmDialog, setConfirmDialog] = useState<
    | {
        title: string
        message: ReactNode
        confirmLabel?: string
        onConfirm: () => void
      }
    | null
  >(null)

  // Rechtsklick-Menue fuer eine Permission-Zelle. null = geschlossen.
  // Position in Viewport-Koordinaten (fixed), damit es nicht vom Tabellen-
  // Overflow abgeschnitten wird. Ziel ist entweder ein Folder oder ein
  // Share-Root (genau eins von beiden gesetzt).
  const [cellMenu, setCellMenu] = useState<
    (PermTarget & { groupId: string; x: number; y: number }) | null
  >(null)
   
  const cellMenuRef = useRef<HTMLDivElement | null>(null)

  // Nach dem Rendern die echten Masse des Menues messen und bei Bedarf in den
  // Viewport zurueckziehen. Die Labels ("DENY – Kein Zugriff",
  // "Einwurf-Postfach", …) machen das Menue breiter als das grobe Pre-Clamp
  // in openCellMenu annimmt, daher hier exakt klammern.
  useLayoutEffect(() => {
    if (!cellMenu || !cellMenuRef.current) return
    const rect = cellMenuRef.current.getBoundingClientRect()
    const pad = 8
     
    const vw = window.innerWidth
     
    const vh = window.innerHeight
    let x = cellMenu.x
    let y = cellMenu.y
    if (rect.right > vw - pad) x = Math.max(pad, vw - rect.width - pad)
    if (rect.bottom > vh - pad) y = Math.max(pad, vh - rect.height - pad)
    if (x !== cellMenu.x || y !== cellMenu.y) {
      setCellMenu((prev) => (prev ? { ...prev, x, y } : prev))
    }
  }, [cellMenu])

  // Escape + Klick ausserhalb schliessen das Zellen-Menue.
  // Wichtig: `click` (nicht `mousedown`) vermeidet die Race-Condition mit
  // onContextMenu — rechte Maustaste triggert kein `click`, nur `contextmenu`.
  useEffect(() => {
    if (!cellMenu) return
     
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setCellMenu(null) }
    const onDocClick = () => setCellMenu(null)
     
    document.addEventListener('keydown', onKey)
     
    document.addEventListener('click', onDocClick)
    return () => {
       
      document.removeEventListener('keydown', onKey)
       
      document.removeEventListener('click', onDocClick)
    }
  }, [cellMenu])

  const selectedServers = useMemo(
    () => servers.filter((s) => selectedServerSet.has(s.id)),
    [servers, selectedServerSet],
  )
  // `primaryServer` = Fallback wenn nur EIN Server aktiv ist, z.B. als
  // Default fuer den Draft-Commit ("welchen Server nehmen wenn keiner eingetippt?")
  // und als Placeholder-Hinweis. Bei mehreren aktiven Servern → null.
  const primaryServer = selectedServers.length === 1 ? selectedServers[0] : null
  const visibleShareIds = useMemo(
    () => new Set(selectedServers.flatMap((s) => s.shares.map((sh) => sh.id))),
    [selectedServers],
  )
  const hasAnyShares = selectedServers.some((s) => s.shares.length > 0)

  const folders = useMemo(
    () => data.folders.filter((f) => visibleShareIds.has(f.shareId)),
    [data.folders, visibleShareIds],
  )
  const groups = data.groups

  // O(1)-Index ueber alle Permissions, neu aufgebaut wenn sich data.permissions
  // aendert. Vorher: jeder getPermission-Call machte `.find()` ueber das gesamte
  // Array (O(P)) — bei 1000 Zellen × 5 Kettenglied-Lookups = O(F·G·D·P) pro
  // Render, was die Matrix bei grossen Strukturen unbenutzbar machte.
  const permIndex = useMemo(() => {
    const m = new Map<string, Permission>()
    for (const p of data.permissions) {
      const k =
        p.folderId !== undefined
          ? `F:${p.folderId}|${p.groupId}`
          : `S:${p.shareId}|${p.groupId}`
      m.set(k, p)
    }
    return m
  }, [data.permissions])

  // O(1)-Lookup fuer Ordner ueber ihre ID — wird in den rekursiven Vererbungs-
  // und Pfad-Checks benutzt, statt jedes Mal `data.folders.find(...)` laufen
  // zu lassen.
  const foldersById = useMemo(
    () => new Map(data.folders.map((f) => [f.id, f] as const)),
    [data.folders],
  )

  // Sichtbare Shares als flache Liste — wird an mehreren Stellen genutzt
  // (z.B. fuer `getInheritedResponsibles` in der Folder-Row) und braucht nur
  // bei Aenderungen an `data.servers` neu berechnet zu werden.
  const allShares = useMemo(
    () => data.servers.flatMap((s) => s.shares),
    [data.servers],
  )

  async function handleExport() {
    const abGroups = data.groups.filter((g) => g.type === 'AB')
    const hint = data.domainPrefix || 'struktur'
    await exportMatrixToExcel(data, abGroups, hint)
  }

  async function handleExportTemplate() {
    const hint = data.domainPrefix ? `${data.domainPrefix}_Vorlage` : 'Vorlage'
    await exportMatrixTemplate(hint)
  }

  function handleDragEnter(e: React.DragEvent) {
    e.preventDefault()
    dragCounterRef.current++
    if (dragCounterRef.current === 1) setIsDragOver(true)
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) setIsDragOver(false)
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    dragCounterRef.current = 0
    setIsDragOver(false)
    const file = e.dataTransfer.files[0]
    if (!file) return
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!ext || !['xlsx', 'xls', 'csv'].includes(ext)) return
    handleImport(file)
  }

  async function handleImport(file: File, replaceExisting = importReplaceExisting) {
    setImportError(null)
    setImportPreview(null)
    setImportFile(file)
    setShowImportModal(true)
    try {
      const parsed = await parseMatrixFile(file)
      const { patch, summary } = applyImport(data, parsed, { replaceExisting })
      setImportPreview({ parsed, summary, patch })
    } catch (e) {
      setImportError(e instanceof Error ? e.message : String(e))
    }
  }

  async function toggleReplaceExisting(value: boolean) {
    setImportReplaceExisting(value)
    if (importFile) {
      await handleImport(importFile, value)
    }
  }

  function confirmImport() {
    if (importPreview) update(importPreview.patch)
    closeImportModal()
  }

  function closeImportModal() {
    setShowImportModal(false)
    setImportPreview(null)
    setImportFile(null)
    setImportError(null)
  }

  function updateDraftRow(index: number, patch: Partial<DraftRow>) {
    setDraftRows((prev) => {
      const arr = [...prev]
      arr[index] = { ...(arr[index] ?? emptyDraft()), ...patch }
      // Nachfolgende leere Rows immer auf mindestens 10 auffuellen, sobald
      // in der letzten Row getippt wird.
      while (arr.length - index < 5) arr.push(emptyDraft())
      return arr
    })
  }

  /**
   * Commitet eine Draft-Zeile: wandelt die Rohwerte in Server/Share/
   * Drive/Folder-Operationen um, mit Auto-Match und Anlegen falls
   * nicht vorhanden. Die Reihenfolge:
   * 1. Server bestimmen (Match per Name, sonst Anlegen wenn serverName gesetzt, sonst primaryServer)
   * 2. Share bestimmen analog (innerhalb des Servers)
   * 3. Drive: per Letter matchen, sonst anlegen (auf Share)
   * 4. Folder: fuer jede gefuellte Ebene einen Folder anlegen (Parent = vorherige Ebene)
   * 5. Verantwortlich: auf tiefsten angelegten Folder oder Drive setzen
   */
  /**
   * Wandelt eine `DraftRow` in konkrete Server/Share/Drive/Folder-Eintraege
   * um und ruft `update(...)` auf. Enthaelt keinen lokalen Draft-State —
   * wird vom Bulk-Draft-Bereich (`draftRows`) aufgerufen.
   *
   * Return: true wenn etwas committet wurde, false wenn die Zeile leer
   * war oder kein Server bestimmt werden konnte.
   */
  function commitDraftData(row: DraftRow): boolean {
    const hasAnything =
      row.driveLetter.trim() ||
      row.driveLabel.trim() ||
      row.serverName.trim() ||
      row.shareName.trim() ||
      row.levelNames.some((v) => v && v.trim()) ||
      row.responsibleEmployees.length > 0
    if (!hasAnything) return false

    // Inheritance-Context: letzter Folder in der angezeigten Reihenfolge
    // liefert Default-Werte (Laufwerk, Benennung, Server, Share) fuer
    // leer gelassene Felder — Excel-artiges "Fortschreiben" der
    // uebergeordneten Zeile.
    //
    // WICHTIG: Wenn der User einen Laufwerksbuchstaben in die Draft-Zeile
    // geschrieben hat, ziehen wir den Kontext NUR aus Foldern, deren
    // effektiver Drive denselben Buchstaben hat. Sonst wuerde eine Zeile
    // mit neuem, noch unbekannten Letter versehentlich in einen fremden
    // Share „hochspringen" — z.B. `X` + Ordnername → landet unter dem
    // Share mit dem global hoechsten Letter (W, MaKs) statt einen neuen
    // X-Mapping-Bereich zu eroeffnen.
    const draftLetter = row.driveLetter.trim().replace(/:$/, '').toUpperCase().slice(0, 1)
    const ctxCandidates = draftLetter
      ? data.folders.filter(
          (f) => (resolveDrive(f)?.letter || '').toUpperCase() === draftLetter,
        )
      : [...data.folders]
    const sortedForCtx = ctxCandidates.sort((a, b) => {
      const da = resolveDrive(a)
      const db = resolveDrive(b)
      const la = (da?.letter || 'ZZ').toUpperCase()
      const lb = (db?.letter || 'ZZ').toUpperCase()
      if (la !== lb) return la.localeCompare(lb)
      return a.relativePath.localeCompare(b.relativePath, 'de', { sensitivity: 'base' })
    })
    const contextFolder = sortedForCtx.length > 0 ? sortedForCtx[sortedForCtx.length - 1] : null
    const contextDrive = contextFolder ? resolveDrive(contextFolder) : null
    const contextServer = contextFolder ? getServerOfShare(contextFolder.shareId) : null
    const contextShare =
      contextServer && contextFolder
        ? contextServer.shares.find((sh) => sh.id === contextFolder.shareId) ?? null
        : null

    const effServerName = row.serverName.trim() || contextServer?.name || ''
    const effShareName = row.shareName.trim() || contextShare?.name || ''
    const effLetter = (row.driveLetter.trim() || contextDrive?.letter || '')
      .replace(/:$/, '')
      .toUpperCase()
      .slice(0, 1)
    const effLabel = row.driveLabel.trim() || contextDrive?.label || ''

    // 1. Server: Match per Name (case-insensitive), sonst Fallback auf
    //    primaryServer (wenn genau EIN Server aktiv ist) — bei mehreren
    //    aktiven Servern muss der User den Namen eingeben.
    const servers = [...data.servers]
    let server = effServerName
      ? servers.find((s) => s.name.toLowerCase() === effServerName.toLowerCase())
      : primaryServer
    if (!server && effServerName) {
      // Neu anlegen
      server = {
        id: crypto.randomUUID(),
        name: effServerName,
        hostname: effServerName,
        shares: [],
      }
      servers.push(server)
    }
    if (!server) {
      setDraftError(
        'Server-Name fehlt: Bei mehreren aktiven Servern muss der Server-Name in der Zeile angegeben werden, damit der Ordner nicht in einen fremden Share rutscht.',
      )
      return false
    }

    // 2. Share: Match per Name, sonst Anlegen. Kein harter 'Daten'-Fallback
    // mehr — wenn auch die uebergeordnete Zeile keine Share hat, nehmen
    // wir den ersten bestehenden Share des Servers.
    const shareName = effShareName || server.shares[0]?.name || 'Daten'
    let share = server.shares.find((s) => s.name.toLowerCase() === shareName.toLowerCase())
    const updatedServers = servers.map((s) => {
      if (s.id !== server!.id) return s
      if (!share) {
        share = {
          id: crypto.randomUUID(),
          name: shareName,
          isHidden: true,
          uncPath: `\\\\${s.hostname || s.name}\\${shareName}$`,
        }
        return { ...s, shares: [...s.shares, share] }
      }
      return s
    })

    // 3. Drive: per Letter matchen (innerhalb des Shares), sonst anlegen
    const letter = effLetter
    let drive = letter
      ? data.drives.find((d) => d.letter.toUpperCase() === letter && d.shareId === share!.id)
      : undefined
    const updatedDrives = [...data.drives]
    if (letter && !drive) {
      drive = {
        id: crypto.randomUUID(),
        shareId: share!.id,
        letter,
        label: effLabel,
        type: 'team',
      }
      updatedDrives.push(drive)
    } else if (drive && effLabel && !drive.label) {
      // Nachtrag Label
      const i = updatedDrives.findIndex((d) => d.id === drive!.id)
      updatedDrives[i] = { ...drive, label: effLabel }
      drive = updatedDrives[i]
    }

    // 4. Folder-Kette: fuer jede gefuellte Ebene einen Folder anlegen.
    // Wenn fruehe Ebenen leer gelassen wurden aber eine spaetere gefuellt
    // ist, wird als Parent der letzte existierende Folder der passenden
    // Tiefe im gleichen Drive/Share uebernommen.
    const allLevels = row.levelNames
    const firstFilledIdx = allLevels.findIndex((v) => v && v.trim())
    const newFolders: Folder[] = []
    if (firstFilledIdx >= 0) {
      let parentId: string | null = null
      if (firstFilledIdx > 0) {
        // Parent = zuletzt angelegter Folder mit Tiefe firstFilledIdx - 1
        // im aktuellen Share (und Drive wenn vorhanden)
        const candidates = data.folders.filter(
          (f) =>
            f.shareId === share!.id &&
            (drive ? resolveDrive(f)?.id === drive.id : true) &&
            folderDepth(f) === firstFilledIdx - 1,
        )
        if (candidates.length > 0) parentId = candidates[candidates.length - 1].id
      }
      for (let i = firstFilledIdx; i < allLevels.length; i++) {
        const val = (allLevels[i] ?? '').trim()
        if (!val) break
        const parent = parentId
          ? newFolders.find((x) => x.id === parentId) ?? data.folders.find((x) => x.id === parentId)
          : null
        const relPath = parent ? `${parent.relativePath}\\${val}` : val
        const f: Folder = {
          id: crypto.randomUUID(),
          shareId: share!.id,
          driveId: drive?.id,
          name: val,
          relativePath: relPath,
          parent: parent?.id ?? null,
        }
        newFolders.push(f)
        parentId = f.id
      }
    }

    // 5. Verantwortliche: tiefster neu angelegter Folder bekommt sie, sonst Drive
    if (row.responsibleEmployees.length > 0) {
      if (newFolders.length > 0) {
        const last = newFolders[newFolders.length - 1]
        last.responsibleEmployees = row.responsibleEmployees
      } else if (drive) {
        const i = updatedDrives.findIndex((d) => d.id === drive!.id)
        if (i >= 0) {
          updatedDrives[i] = {
            ...updatedDrives[i],
            responsibleEmployees: row.responsibleEmployees,
          }
        }
      }
    }

    update({
      servers: updatedServers,
      drives: updatedDrives,
      folders: newFolders.length > 0 ? [...data.folders, ...newFolders] : data.folders,
    })

    return true
  }

  /** Commitet eine Bulk-Draft-Zeile und leert sie anschliessend. */
  function commitDraftRow(index: number) {
    const row = draftRows[index]
    if (!row) return
    setDraftError(null)
    const ok = commitDraftData(row)
    if (!ok) return
    setDraftRows((prev) => {
      const arr = [...prev]
      arr[index] = emptyDraft()
      while (arr.length < 10) arr.push(emptyDraft())
      return arr
    })
  }

  function openSubDraft(parentId: string) {
    setSubDrafts((prev) => (parentId in prev ? prev : { ...prev, [parentId]: '' }))
  }
  function updateSubDraft(parentId: string, name: string) {
    setSubDrafts((prev) => ({ ...prev, [parentId]: name }))
  }
  function closeSubDraft(parentId: string) {
    setSubDrafts((prev) => {
      if (!(parentId in prev)) return prev
      const next = { ...prev }
      delete next[parentId]
      return next
    })
  }
  function commitSubDraft(key: string) {
    const name = (subDrafts[key] ?? '').trim()
    if (!name) { closeSubDraft(key); return }
    // Share-Root Draft: Key = "share:<shareId>" → Top-Level-Folder im Share
    if (key.startsWith('share:')) {
      const shareId = key.slice('share:'.length)
      const firstDrive = data.drives.find((d) => d.shareId === shareId)
      const newFolder: Folder = {
        id: crypto.randomUUID(),
        shareId,
        driveId: firstDrive?.id,
        name,
        relativePath: name,
        parent: null,
      }
      update({ folders: [...data.folders, newFolder] })
      closeSubDraft(key)
      return
    }
    // Folder-Child Draft: Key = Folder-ID
    const parent = data.folders.find((f) => f.id === key)
    if (!parent) { closeSubDraft(key); return }
    const newFolder: Folder = {
      id: crypto.randomUUID(),
      shareId: parent.shareId,
      name,
      relativePath: `${parent.relativePath}\\${name}`,
      parent: parent.id,
    }
    update({ folders: [...data.folders, newFolder] })
    closeSubDraft(key)
  }

  /**
   * Gibt den Server zu einer ShareId zurueck (via nested search).
   */
  function getServerOfShare(shareId: string) {
    return data.servers.find((s) => s.shares.some((sh) => sh.id === shareId))
  }

  function updateFolder(id: string, patch: Partial<Folder>) {
    // If name changed, also update relativePath + all descendants' relativePaths
    const current = data.folders.find((f) => f.id === id)
    if (!current) return
    let newFolders = data.folders.map((f) => (f.id === id ? { ...f, ...patch } : f))
    // Map alter → neuer Pfad, damit wir anschliessend Drive.subPath mitziehen koennen.
    const pathMoves: Array<{ shareId: string; from: string; to: string }> = []
    if (patch.name && patch.name !== current.name) {
      const updated = newFolders.find((f) => f.id === id)!
      const parent = updated.parent ? newFolders.find((f) => f.id === updated.parent) : null
      const newRelPath = parent ? `${parent.relativePath}\\${updated.name}` : updated.name
      pathMoves.push({ shareId: updated.shareId, from: current.relativePath, to: newRelPath })
      newFolders = newFolders.map((f) => (f.id === id ? { ...f, relativePath: newRelPath } : f))
      // Update descendants
      const updateDescendants = (parentId: string, parentPath: string) => {
        for (const child of newFolders.filter((f) => f.parent === parentId)) {
          const childNewPath = `${parentPath}\\${child.name}`
          pathMoves.push({ shareId: child.shareId, from: child.relativePath, to: childNewPath })
          newFolders = newFolders.map((f) => (f.id === child.id ? { ...f, relativePath: childNewPath } : f))
          updateDescendants(child.id, childNewPath)
        }
      }
      updateDescendants(id, newRelPath)
    }
    if (pathMoves.length > 0) {
      const moveIndex = new Map(pathMoves.map((m) => [`${m.shareId}|${m.from}`, m.to]))
      const newDrives = data.drives.map((d) => {
        const key = `${d.shareId}|${d.subPath ?? ''}`
        const to = moveIndex.get(key)
        return to !== undefined ? { ...d, subPath: to } : d
      })
      update({ folders: newFolders, drives: newDrives })
      return
    }
    update({ folders: newFolders })
  }

  function updateShare(shareId: string, patch: Partial<Share>) {
    const servers = data.servers.map((s) => ({
      ...s,
      shares: s.shares.map((sh) => (sh.id === shareId ? { ...sh, ...patch } : sh)),
    }))
    update({ servers })
  }

  /**
   * Entfernt einen einzelnen Drive aus `data.drives` und loest die Referenz
   * in allen Foldern (`driveId`) auf. Wird aus dem Duplikat/Orphan-Banner
   * aufgerufen, damit der User konfliktaere oder verwaiste Mappings direkt
   * aus der Matrix-Ansicht saeubern kann — der Tab "Laufwerke" ist in der
   * Kompakt-Ansicht nicht verfuegbar.
   */
  function deleteDriveById(id: string) {
    update({
      drives: data.drives.filter((d) => d.id !== id),
      folders: data.folders.map((f) => (f.driveId === id ? { ...f, driveId: undefined } : f)),
    })
  }

  function updateDrive(id: string, patch: Partial<Drive>) {
    update({
      drives: data.drives.map((d) => {
        if (d.id !== id) return d
        const merged: Drive = { ...d, ...patch }
        if (patch.letter !== undefined) {
          merged.letter = patch.letter.toUpperCase().slice(0, 1)
        }
        return merged
      }),
    })
  }

  // Legt ein Drive fuer einen Share an, wenn der User LW/Benennung direkt in
  // der Matrix-Zeile befuellt und bisher noch kein Drive gemappt ist.
  function createDriveForShare(shareId: string, patch: { letter?: string; label?: string }) {
    const letter = (patch.letter ?? '').toUpperCase().slice(0, 1)
    const drive: Drive = {
      id: crypto.randomUUID(),
      shareId,
      letter,
      label: patch.label ?? '',
      subPath: '',
      type: 'team',
    }
    update({ drives: [...data.drives, drive] })
  }

  function deleteFolder(id: string) {
    // Collect self + all descendants
    const toDelete = new Set<string>([id])
    const collect = (parentId: string) => {
      for (const f of data.folders.filter((x) => x.parent === parentId)) {
        toDelete.add(f.id)
        collect(f.id)
      }
    }
    collect(id)
    // Drives, die direkt auf einen dieser Ordner gemappt sind (via subPath
    // === relativePath), sind nach dem Loeschen verwaist → ebenfalls entfernen.
    const deletedRelPaths = new Set(
      data.folders.filter((f) => toDelete.has(f.id)).map((f) => f.relativePath),
    )
    const deletedShareIds = new Set(
      data.folders.filter((f) => toDelete.has(f.id)).map((f) => f.shareId),
    )
    const drivesToDelete = new Set(
      data.drives
        .filter((d) => deletedShareIds.has(d.shareId) && deletedRelPaths.has(d.subPath ?? ''))
        .map((d) => d.id),
    )
    update({
      folders: data.folders.filter((f) => !toDelete.has(f.id)),
      drives: data.drives.filter((d) => !drivesToDelete.has(d.id)),
      permissions: data.permissions.filter((p) => !p.folderId || !toDelete.has(p.folderId)),
    })
  }

  /**
   * Loescht einen kompletten Share inklusive aller zugehoerigen Drives,
   * Ordner und Berechtigungen. Entfernt den Share aus dem Server-Objekt.
   */
  function deleteShare(shareId: string) {
    const folderIdsInShare = new Set(
      data.folders.filter((f) => f.shareId === shareId).map((f) => f.id),
    )
    update({
      servers: data.servers.map((s) => ({
        ...s,
        shares: s.shares.filter((sh) => sh.id !== shareId),
      })),
      drives: data.drives.filter((d) => d.shareId !== shareId),
      folders: data.folders.filter((f) => f.shareId !== shareId),
      permissions: data.permissions.filter((p) => {
        if (p.shareId === shareId) return false
        if (p.folderId && folderIdsInShare.has(p.folderId)) return false
        return true
      }),
    })
  }

  /**
   * Liefert den Drive, der DIREKT auf diesen Ordner gemappt ist — das ist
   * der Drive mit `shareId === folder.shareId` und `subPath === folder.relativePath`.
   * Unterscheidet sich von `resolveDrive`, das auch geerbte Drives
   * (von Eltern-Ordnern / Share-Root) liefert.
   */
  function getFolderOwnDrive(folder: Folder): Drive | null {
    return (
      data.drives.find(
        (d) => d.shareId === folder.shareId && (d.subPath ?? '') === folder.relativePath,
      ) ?? null
    )
  }

  /**
   * Liefert den Drive, der auf den Share-Root gemappt ist (subPath leer).
   */
  function getShareRootDrive(shareId: string): Drive | null {
    return (
      data.drives.find((d) => d.shareId === shareId && !d.subPath) ?? null
    )
  }

  /**
   * Setzt oder aktualisiert den Drive fuer einen bestimmten Ordner. Legt
   * bei Bedarf einen neuen Drive mit `subPath === folder.relativePath` an.
   * Wenn Letter UND Label leer werden, wird der Drive geloescht.
   */
  function setFolderDrivePatch(folder: Folder, patch: { letter?: string; label?: string }) {
    const existing = getFolderOwnDrive(folder)
    if (existing) {
      const nextLetter =
        patch.letter !== undefined
          ? patch.letter.toUpperCase().slice(0, 1)
          : existing.letter
      const nextLabel = patch.label !== undefined ? patch.label : existing.label
      if (!nextLetter && !nextLabel) {
        // Leer → Drive entfernen, Folder.driveId loesen
        update({
          drives: data.drives.filter((d) => d.id !== existing.id),
          folders: data.folders.map((f) =>
            f.driveId === existing.id ? { ...f, driveId: undefined } : f,
          ),
        })
      } else {
        update({
          drives: data.drives.map((d) =>
            d.id === existing.id ? { ...d, letter: nextLetter, label: nextLabel } : d,
          ),
        })
      }
      return
    }
    // Noch kein Drive fuer diesen Ordner — neu anlegen
    const letter = (patch.letter ?? '').toUpperCase().slice(0, 1)
    const label = patch.label ?? ''
    if (!letter && !label) return
    const drive: Drive = {
      id: crypto.randomUUID(),
      shareId: folder.shareId,
      letter,
      label,
      subPath: folder.relativePath,
      type: 'team',
    }
    update({
      drives: [...data.drives, drive],
      folders: data.folders.map((f) =>
        f.id === folder.id ? { ...f, driveId: drive.id } : f,
      ),
    })
  }

  function matchesTarget(p: Permission, target: PermTarget): boolean {
    if (target.folderId !== undefined) return p.folderId === target.folderId
    return !p.folderId && p.shareId === target.shareId
  }

  function getPermission(target: PermTarget, groupId: string): Rights | null {
    return permIndex.get(permTargetKey(target, groupId))?.rights ?? null
  }

  function setPermission(target: PermTarget, groupId: string, rights: Rights | null) {
    let perms = data.permissions.filter((p) => !(matchesTarget(p, target) && p.groupId === groupId))
    if (rights !== null) {
      const perm: Permission =
        target.folderId !== undefined
          ? { folderId: target.folderId, groupId, rights }
          : { shareId: target.shareId, groupId, rights }
      perms = [...perms, perm]
    }
    update({ permissions: perms })
  }

  // Cycle-Reihenfolgen fuer Linksklick in der Permission-Zelle.
  // Normal-Mode: 6 Presets nach typischer Nutzung sortiert.
  // Advanced-Mode: die 7 NTFS-Stufen + null (vererben).
  // Reihenfolge so gewaehlt, dass der erste Linksklick auf eine geerbte Zelle
  // direkt zu „Entziehen" fuehrt (haeufigster Wunsch beim Klicken auf einen
  // bereits geerbten Wert: Vererbung fuer diese Gruppe unterbrechen).
  const PRESET_CYCLE: PermissionPreset[] = [
    'inherit', 'revoke', 'modify', 'read', 'deny', 'dropbox', 'full',
  ]
  const RIGHTS_CYCLE: Array<Rights | null> = [
    null, 'Revoke', 'FullControl', 'Modify', 'ReadExecute', 'Read', 'Write', 'ListFolder', 'None',
  ]

  function cyclePermission(target: PermTarget, groupId: string, backward: boolean) {
    const direct = getPermission(target, groupId)
    if (advancedMode) {
      const idx = RIGHTS_CYCLE.indexOf(direct)
      const base = idx === -1 ? 0 : idx
      const step = backward ? -1 : 1
      const next = RIGHTS_CYCLE[(base + step + RIGHTS_CYCLE.length) % RIGHTS_CYCLE.length]
      setPermission(target, groupId, next)
    } else {
      const currentPreset: PermissionPreset = direct ? rightsToPreset(direct) : 'inherit'
      const idx = PRESET_CYCLE.indexOf(currentPreset)
      const base = idx === -1 ? 0 : idx
      const step = backward ? -1 : 1
      const next = PRESET_CYCLE[(base + step + PRESET_CYCLE.length) % PRESET_CYCLE.length]
      const def = presetByValue(next)
      setPermission(target, groupId, def.rights)
    }
  }

   
  function openCellMenu(e: React.MouseEvent, target: PermTarget, groupId: string) {
    e.preventDefault()
    e.stopPropagation()
     
    const maxX = Math.max(0, window.innerWidth - 240)
     
    const maxY = Math.max(0, window.innerHeight - 260)
    setCellMenu({
      ...target,
      groupId,
      x: Math.min(e.clientX, maxX),
      y: Math.min(e.clientY, maxY),
    } as PermTarget & { groupId: string; x: number; y: number })
  }

  /**
   * Bulk-Apply: setzt ein Preset auf alle sichtbaren Ordner der aktuell
   * gewaehlten Server-Sektion fuer eine bestimmte Gruppe. Bestehende Eintraege
   * werden ueberschrieben. Bei `preset === 'inherit'` werden alle Eintraege
   * fuer diese Gruppe + diese Ordner entfernt (Zurueck zur Vererbung).
   */
  function bulkApplyPresetToGroup(groupId: string, preset: PermissionPreset) {
    const def = presetByValue(preset)
    const visibleFolderIds = folders.map((f) => f.id)
    const visibleShareIdList = [...visibleShareIds]
    const count = visibleFolderIds.length + visibleShareIdList.length
    if (count === 0) return
    const group = groups.find((g) => g.id === groupId)
    const groupLabel = group ? `${group.type}_${group.name}` : groupId
    const confirmMsg =
      `Preset »${def.label}« auf ${count} Ordner/Shares für Gruppe »${groupLabel}« anwenden?\n\n` +
      `Bestehende Einträge werden überschrieben.`
     
    if (!confirm(confirmMsg)) return

    const visibleFolderSet = new Set(visibleFolderIds)
    const visibleShareSet = new Set(visibleShareIdList)
    let perms = data.permissions.filter((p) => {
      if (p.groupId !== groupId) return true
      if (p.folderId && visibleFolderSet.has(p.folderId)) return false
      if (!p.folderId && p.shareId && visibleShareSet.has(p.shareId)) return false
      return true
    })
    if (def.rights !== null) {
      const rights = def.rights as Rights
      const folderPerms: Permission[] = visibleFolderIds.map((folderId) => ({ folderId, groupId, rights }))
      const sharePerms: Permission[] = visibleShareIdList.map((shareId) => ({ shareId, groupId, rights }))
      perms = [...perms, ...folderPerms, ...sharePerms]
    }
    update({ permissions: perms })
    setBulkMenu(null)
  }

  /**
   * Hilfs-Lookup fuer den VOLLEN Permission-Eintrag (inkl. `noInherit`-Flag).
   * Wird fuer die Vererbungs-Resolution benoetigt.
   */
  function getPermissionEntry(target: PermTarget, groupId: string): Permission | null {
    return permIndex.get(permTargetKey(target, groupId)) ?? null
  }

  /**
   * Vererbungskette: Folder → Parent → … → Share-Root. Gibt das erste
   * explizit gesetzte Recht in der Kette zurueck, das auch tatsaechlich vererbt —
   * Eintraege mit `noInherit: true` (NTFS-Scope „Nur dieser Ordner") werden
   * beim Hochlaufen uebersprungen, damit z.B. ein Traversal-Recht am
   * Share-Root nicht ungewollt an Geschwister-Ordner gelangt.
   */
  function getInheritedRights(folderId: string, groupId: string): Rights | null {
    const folder = foldersById.get(folderId)
    if (!folder) return null
    if (folder.parent) {
      const entry = getPermissionEntry({ folderId: folder.parent }, groupId)
      if (entry && !entry.noInherit) return entry.rights
      return getInheritedRights(folder.parent, groupId)
    }
    const shareEntry = getPermissionEntry({ shareId: folder.shareId }, groupId)
    if (shareEntry && !shareEntry.noInherit) return shareEntry.rights
    return null
  }

  /**
   * Plausibilitaetspruefung: Wenn eine Gruppe auf einem Ordner Zugriff hat
   * (egal ob direkt oder geerbt), muss sie auch ALLE Vorfahren durchwandern
   * koennen — Share-Root und jeden Parent-Folder. Wird ein Vorfahre per DENY,
   * Revoke oder gar nicht aufgefuehrt, landet der Zugriff im Leeren: Windows
   * verweigert das Durchwandern und die Rechte am Zielordner sind praktisch
   * wirkungslos.
   *
   * Gibt eine Fehlerbeschreibung zurueck, wenn die Zugriffskette gebrochen
   * ist — oder null wenn alles plausibel ist.
   */
  function checkFolderAccessPath(
    folderId: string,
    groupId: string,
  ): { ancestorName: string; issue: string } | null {
    const folder = foldersById.get(folderId)
    if (!folder) return null
    // Effektive Rechte auf dem Zielordner. Ohne Zugriff ist die Kette
    // irrelevant — der User soll sowieso nicht hinein.
    const directOnFolder = getPermission({ folderId }, groupId)
    const inheritedOnFolder = directOnFolder ? null : getInheritedRights(folderId, groupId)
    const effective = directOnFolder ?? inheritedOnFolder
    if (!effective || effective === 'None' || effective === 'Revoke') return null

    const describe = (r: Rights | null): string => {
      if (!r) return 'keine Berechtigung'
      if (r === 'None') return 'DENY (kein Zugriff)'
      if (r === 'Revoke') return 'Entzogen (kein ACL-Eintrag)'
      return ''
    }

    // Alle Parent-Folder pruefen (von unten nach oben)
    let current: Folder = folder
    while (current.parent) {
      const parent = foldersById.get(current.parent)
      if (!parent) break
      const pDirect = getPermission({ folderId: parent.id }, groupId)
      const pInherited = pDirect ? null : getInheritedRights(parent.id, groupId)
      const pEffective = pDirect ?? pInherited
      if (!pEffective || pEffective === 'None' || pEffective === 'Revoke') {
        return { ancestorName: parent.name, issue: describe(pEffective) }
      }
      current = parent
    }

    // Share-Root (Top der Kette, keine weitere Vererbung darueber)
    const shareDirect = getPermission({ shareId: folder.shareId }, groupId)
    if (!shareDirect || shareDirect === 'None' || shareDirect === 'Revoke') {
      return { ancestorName: 'Share-Root', issue: describe(shareDirect) }
    }

    return null
  }

  /**
   * Ermittelt den effektiven Drive eines Ordners — direkt via f.driveId
   * oder geerbt vom ersten Vorfahren, der einen hat.
   */
  function resolveDrive(folder: Folder): Drive | null {
    const direct = folder.driveId
      ? data.drives.find((d) => d.id === folder.driveId)
      : null
    if (direct) return direct
    if (folder.parent) {
      const parent = foldersById.get(folder.parent)
      if (parent) return resolveDrive(parent)
    }
    return null
  }

  /** Folder-Tiefe (0 = Top-Level, 1 = Ebene 2, 2+ = Sub-Sub-…). */
  function folderDepth(folder: Folder): number {
    let d = 0
    let cur: Folder | undefined = folder
    while (cur?.parent) {
      const p = foldersById.get(cur.parent)
      if (!p) break
      cur = p
      d++
    }
    return d
  }

  /**
   * Baut die flache Zeilen-Reihenfolge fuer die Excel-artige Matrix auf:
   * pro Drive einen Section-Header gefolgt von allen Foldern in
   * Hierarchie-Reihenfolge (depth-first). Folders ohne Drive landen am
   * Ende in einer "Ohne Laufwerk"-Sektion.
   */

  // Abteilungs-Gruppen werden als Spalten der Matrix angezeigt; die uebrigen
  // Gruppen-Typen sind Hinweise am Footer. Memoisiert, damit die Referenz
  // stabil bleibt und nachfolgende Memos nicht unnoetig neu rechnen.
  const abGroups = useMemo(() => groups.filter((g) => g.type === 'AB'), [groups])
  const otherGroups = useMemo(() => groups.filter((g) => g.type !== 'AB'), [groups])

  // Laufwerksbuchstaben-Duplikate ueber alle Drives hinweg erkennen, damit
  // doppelte Z/Y/... in den LW-Inputs rot markiert werden koennen.
  const driveLetterCounts = useMemo(() => {
    const m = new Map<string, number>()
    for (const d of data.drives) {
      const l = (d.letter || '').toUpperCase()
      if (!l) continue
      m.set(l, (m.get(l) ?? 0) + 1)
    }
    return m
  }, [data.drives])
  const isDuplicateLetter = useCallback(
    (letter: string | undefined): boolean => {
      const l = (letter || '').toUpperCase()
      if (!l) return false
      return (driveLetterCounts.get(l) ?? 0) > 1
    },
    [driveLetterCounts],
  )

  // Plausibilitaetspruefung: Fuer jedes (Folder, Group)-Paar pruefen, ob die
  // Zugriffskette bis Share-Root durchgaengig ist. Wird pro Zelle gebraucht,
  // daher zwingend memoisieren — sonst laeuft die F × G × D²-Schleife bei
  // jedem Re-Render neu.
  const accessIssues = useMemo(() => {
    const out = new Map<string, Map<string, { ancestorName: string; issue: string }>>()
    for (const f of folders) {
      const perGroup = new Map<string, { ancestorName: string; issue: string }>()
      for (const g of abGroups) {
        const res = checkFolderAccessPath(f.id, g.id)
        if (res) perGroup.set(g.id, res)
      }
      if (perGroup.size > 0) out.set(f.id, perGroup)
    }
    return out
    // `checkFolderAccessPath` liest indirekt aus permIndex + foldersById, die
    // ihrerseits von data.permissions bzw. data.folders abhaengen. Daher reichen
    // diese Deps aus, um Staleness zu vermeiden.
  }, [folders, abGroups, permIndex, foldersById])

  const totalIssues = useMemo(() => {
    let n = 0
    for (const m of accessIssues.values()) n += m.size
    return n
  }, [accessIssues])

  // Zeilen-Reihenfolge: pro sichtbarem Share eine Share-Root-Zeile, gefolgt
  // von den Foldern dieses Shares in Hierarchie-Reihenfolge (Parents vor
  // Kindern, Geschwister alphabetisch). Shares ohne Folders erscheinen
  // trotzdem mit einer Share-Root-Zeile, damit Berechtigungen direkt am
  // Share-Root gesetzt werden koennen.
  type ShareRow = { kind: 'share'; shareId: string }
  type FolderRow = { kind: 'folder'; folder: Folder }
  type Row = ShareRow | FolderRow
  const rowItems = useMemo<Row[]>(() => {
    const byParent = new Map<string, Folder[]>()
    for (const f of folders) {
      const key = `${f.shareId}::${f.parent ?? ''}`
      if (!byParent.has(key)) byParent.set(key, [])
      byParent.get(key)!.push(f)
    }
    for (const list of byParent.values()) {
      list.sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }))
    }
    const walk = (shareId: string, parentId: string | null, out: Row[]) => {
      const children = byParent.get(`${shareId}::${parentId ?? ''}`) || []
      for (const c of children) {
        out.push({ kind: 'folder', folder: c })
        walk(shareId, c.id, out)
      }
    }
    // Alle sichtbaren Shares, nicht nur solche mit Foldern.
    const shareOrder: { shareId: string; driveKey: string; shareName: string }[] = []
    for (const srv of selectedServers) {
      for (const sh of srv.shares) {
        // Stellvertreter-Drive fuer die Sortierung: erster Drive, der auf
        // diesen Share zeigt. Shares ohne Drive landen am Ende.
        const firstDrive = data.drives.find((d) => d.shareId === sh.id)
        shareOrder.push({
          shareId: sh.id,
          driveKey: (firstDrive?.letter || 'ZZ').toUpperCase(),
          shareName: sh.name,
        })
      }
    }
    shareOrder.sort((a, b) => {
      if (a.driveKey !== b.driveKey) return a.driveKey.localeCompare(b.driveKey)
      return a.shareName.localeCompare(b.shareName, 'de', { sensitivity: 'base' })
    })
    const out: Row[] = []
    for (const s of shareOrder) {
      out.push({ kind: 'share', shareId: s.shareId })
      walk(s.shareId, null, out)
    }
    return out
  }, [folders, selectedServers, data.drives])

  // Stabile Callback-Referenzen fuer <PermissionCell>, damit die memoisierten
  // Zellen nicht bei jedem Render neu rendern. Die eigentlichen Funktionen
  // (cyclePermission, openCellMenu) schliessen ueber `data` und wechseln daher
  // ihre Identitaet bei jedem Klick — per Ref-Pattern bleibt der an die Zelle
  // gereichte Callback aber stabil.
  const cyclePermissionRef = useRef(cyclePermission)
  useEffect(() => {
    cyclePermissionRef.current = cyclePermission
  })
  const openCellMenuRef = useRef(openCellMenu)
  useEffect(() => {
    openCellMenuRef.current = openCellMenu
  })
  const onCellCycle = useCallback(
    (t: PermTarget, g: string, b: boolean) => cyclePermissionRef.current(t, g, b),
    [],
  )
  const onCellOpenMenu = useCallback(
    (e: React.MouseEvent, t: PermTarget, g: string) => openCellMenuRef.current(e, t, g),
    [],
  )

  return (
    <div
      className="space-y-4 relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="absolute inset-0 z-40 bg-brand-500/10 border-2 border-dashed border-brand-500 rounded-xl flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-3 text-brand-700 border border-brand-200">
            <Upload className="w-14 h-14 text-brand-500" />
            <p className="text-h3 font-semibold">Excel / CSV hier ablegen</p>
            <p className="text-small text-gray-500">.xlsx · .xls · .csv</p>
          </div>
        </div>
      )}
      {/* Toolbar: Server Selector (Tags) + Excel Import/Export */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-small font-medium text-gray-700 shrink-0">Server:</label>
        <div className="flex items-center gap-1.5 flex-wrap">
          {servers.length > 1 && (
            <FilterChip
              active={allSelected}
              onClick={allSelected ? clearServers : selectAllServers}
              title={allSelected ? 'Alle Server abwählen' : 'Alle Server auswählen'}
            >
              Alle
            </FilterChip>
          )}
          {servers.map((s) => {
            const active = selectedServerSet.has(s.id)
            return (
              <FilterChip
                key={s.id}
                active={active}
                count={s.shares.length}
                onClick={() => toggleServer(s.id)}
                title={active ? `${s.name} ausblenden` : `${s.name} einblenden`}
              >
                {s.name}
              </FilterChip>
            )
          })}
          {servers.length === 0 && (
            <span className="text-caption text-gray-400 italic">
              Noch keine Server angelegt – im Tab »Server & Shares« anlegen.
            </span>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) handleImport(f)
            e.target.value = ''
          }}
        />
        <div className="ml-auto flex items-center gap-2">
          <Dropdown>
            <DropdownTrigger asChild>
              <Button
                variant="primary"
                size="sm"
                leadingIcon={<FileSpreadsheet className="w-4 h-4" />}
                trailingIcon={<ChevronDown className="w-4 h-4" />}
              >
                Excel
              </Button>
            </DropdownTrigger>
            <DropdownContent align="end" className="min-w-[280px]">
              <DropdownLabel>Export</DropdownLabel>
              <DropdownItem onSelect={() => { void handleExportTemplate() }}>
                <FilePlus2 className="w-4 h-4 text-brand-500" />
                <div className="flex flex-col">
                  <span className="font-medium">Vorlage exportieren</span>
                  <span className="text-caption text-gray-500">Leere Vorlage mit Beispiel-Zeilen</span>
                </div>
              </DropdownItem>
              <DropdownItem onSelect={() => { void handleExport() }}>
                <FileDown className="w-4 h-4 text-brand-500" />
                <div className="flex flex-col">
                  <span className="font-medium">Bestehende Struktur exportieren</span>
                  <span className="text-caption text-gray-500">Aktuelle Matrix als .xlsx</span>
                </div>
              </DropdownItem>
              <DropdownSeparator />
              <DropdownLabel>Import</DropdownLabel>
              <DropdownItem onSelect={() => fileInputRef.current?.click()}>
                <Upload className="w-4 h-4 text-brand-500" />
                <div className="flex flex-col">
                  <span className="font-medium">Struktur importieren</span>
                  <span className="text-caption text-gray-500">Datei wählen oder Excel auf die Matrix ziehen</span>
                </div>
              </DropdownItem>
            </DropdownContent>
          </Dropdown>
        </div>
      </div>

      {noneSelected ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-small text-gray-400">
          {servers.length === 0
            ? 'Noch keine Server angelegt.'
            : 'Bitte mindestens einen Server-Tag aktivieren.'}
        </div>
      ) : !hasAnyShares ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-small text-gray-400">
          Die gewählten Server haben noch keine Freigaben. Im Tab "Server & Shares" anlegen.
        </div>
      ) : (
        <>
          {/* Duplikate-Warnung: Laufwerksbuchstaben die mehrfach vergeben sind.
              Pro Workstation kann es jeden Buchstaben nur einmal geben, deshalb
              muessen solche Konflikte aufgeloest werden bevor GPOs generiert werden.
              Wichtig: wir listen AUCH Drives mit auf, die auf ausgeblendeten
              Servern oder verwaisten Shares/Ordnern sitzen — sonst wundert sich
              der User, warum ein Buchstabe rot ist obwohl in der sichtbaren
              Matrix nur eine Zuordnung steht. */}
          {(() => {
            type Loc = { driveId: string; label: string; orphan: boolean }
            const byLetter = new Map<string, Loc[]>()
            for (const d of data.drives) {
              const l = (d.letter || '').toUpperCase()
              if (!l) continue
              const srv = data.servers.find((s) => s.shares.some((sh) => sh.id === d.shareId))
              const share = srv?.shares.find((sh) => sh.id === d.shareId)
              const sub = d.subPath ?? ''
              // Als "orphan" zaehlen: Share existiert nicht mehr, ODER subPath
              // zeigt auf einen Ordner-Pfad der in diesem Share nicht existiert
              // (uebrig von einem geloeschten/umbenannten Ordner).
              const subMatchesFolder =
                !sub ||
                data.folders.some(
                  (f) => f.shareId === d.shareId && f.relativePath === sub,
                )
              const orphan = !share || !subMatchesFolder
              const locLabel = share
                ? `${srv?.name ?? '?'} \\ ${share.name}${share.isHidden ? '$' : ''}${sub ? ` \\ ${sub}` : ' (Share-Root)'}`
                : '(verwaist — Share existiert nicht mehr)'
              if (!byLetter.has(l)) byLetter.set(l, [])
              byLetter.get(l)!.push({ driveId: d.id, label: locLabel, orphan })
            }
            const dupes = [...byLetter.entries()].filter(([, locs]) => locs.length > 1)
            const orphanDrives = [...byLetter.values()].flat().filter((loc) => loc.orphan)
            if (dupes.length === 0 && orphanDrives.length === 0) return null
            function requestDeleteDrive(loc: Loc) {
              setConfirmDialog({
                title: 'Laufwerks-Mapping löschen',
                confirmLabel: 'Mapping entfernen',
                message: (
                  <div className="space-y-2">
                    <p>Soll dieses Laufwerks-Mapping entfernt werden?</p>
                    <p className="text-xs text-gray-600 font-mono bg-gray-50 border border-gray-200 rounded px-2 py-1">
                      {loc.label}
                    </p>
                    <p className="text-xs text-gray-600">
                      Shares und Ordner bleiben erhalten — nur der Laufwerksbuchstabe wird gelöst.
                    </p>
                  </div>
                ),
                onConfirm: () => {
                  deleteDriveById(loc.driveId)
                  setConfirmDialog(null)
                },
              })
            }
            function requestCleanupOrphans(ids: string[]) {
              setConfirmDialog({
                title: 'Verwaiste Mappings entfernen',
                confirmLabel: `${ids.length} Mapping${ids.length > 1 ? 's' : ''} entfernen`,
                message: (
                  <p>
                    Sollen alle {ids.length} verwaisten Laufwerks-Mappings entfernt werden?
                    Sie zeigen auf Shares/Ordner, die nicht mehr existieren.
                  </p>
                ),
                onConfirm: () => {
                  update({
                    drives: data.drives.filter((d) => !ids.includes(d.id)),
                    folders: data.folders.map((f) =>
                      f.driveId && ids.includes(f.driveId) ? { ...f, driveId: undefined } : f,
                    ),
                  })
                  setConfirmDialog(null)
                },
              })
            }
            const orphanIds = orphanDrives.map((l) => l.driveId)
            return (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-small text-red-800 flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0 space-y-2">
                  {dupes.length > 0 && (
                    <div>
                      <p className="font-medium">
                        Laufwerksbuchstabe{dupes.length > 1 ? 'n' : ''} mehrfach vergeben
                      </p>
                      <ul className="mt-1 text-caption space-y-1">
                        {dupes.map(([letter, locs]) => (
                          <li key={letter}>
                            <span className="font-mono font-bold">{letter}:</span> ({locs.length}×)
                            <ul className="ml-3 text-red-700 space-y-0.5">
                              {locs.map((loc) => (
                                <li key={loc.driveId} className="flex items-center gap-1">
                                  <span className="flex-1 min-w-0">
                                    <span className="mr-1">•</span>
                                    {loc.label}
                                    {loc.orphan && (
                                      <span className="ml-1 text-amber-700">
                                        — Ordner/Share fehlt, wahrscheinlich verwaist
                                      </span>
                                    )}
                                  </span>
                                  <IconButton
                                    variant="danger"
                                    size="xs"
                                    icon={<Trash2 />}
                                    aria-label="Dieses Mapping entfernen"
                                    title="Dieses Mapping entfernen"
                                    onClick={() => requestDeleteDrive(loc)}
                                  />
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                      </ul>
                      <p className="text-caption mt-1 text-red-700">
                        Pro Workstation kann jeder Buchstabe nur einmal gemappt werden.
                      </p>
                    </div>
                  )}
                  {orphanDrives.length > 0 && dupes.length === 0 && (
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium">Verwaiste Laufwerks-Mappings gefunden</p>
                        <Button
                          variant="danger"
                          size="xs"
                          onClick={() => requestCleanupOrphans(orphanIds)}
                        >
                          Alle verwaisten entfernen ({orphanIds.length})
                        </Button>
                      </div>
                      <ul className="mt-1 text-caption text-red-700 space-y-0.5">
                        {orphanDrives.map((loc) => (
                          <li key={loc.driveId} className="flex items-center gap-1">
                            <span className="flex-1 min-w-0">
                              <span className="mr-1">•</span>
                              {loc.label}
                            </span>
                            <IconButton
                              variant="danger"
                              size="xs"
                              icon={<Trash2 />}
                              aria-label="Dieses Mapping entfernen"
                              title="Dieses Mapping entfernen"
                              onClick={() => requestDeleteDrive(loc)}
                            />
                          </li>
                        ))}
                      </ul>
                      <p className="text-caption mt-1 text-red-700">
                        Diese Drives zeigen auf Shares/Ordner die nicht mehr existieren.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}

          {/* Fehler aus Draft-Row Commit (z.B. Server-Name fehlt bei mehreren aktiven Servern). */}
          {draftError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-small text-red-800 flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-medium">Zeile konnte nicht übernommen werden</p>
                <p className="text-caption mt-0.5">{draftError}</p>
              </div>
              <IconButton
                variant="danger"
                size="xs"
                icon={<X />}
                aria-label="Schließen"
                title="Schließen"
                onClick={() => setDraftError(null)}
              />
            </div>
          )}

          {/* Hinweis-Banner wenn noch keine Gruppen existieren */}
          {groups.length === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-small text-amber-800 flex items-start gap-3 flex-wrap">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-[200px]">
                <p className="font-medium">Noch keine Gruppen angelegt.</p>
                <p className="text-caption mt-0.5">Leg eine an per "+ Gruppe" unten, per Excel-Import, oder im Tab "Gruppen & User".</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="xs"
                  leadingIcon={<Upload className="w-3.5 h-3.5" />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Excel importieren
                </Button>
              </div>
            </div>
          )}


          {/* Zugriffsmatrix — flaches Sheet-Layout (Excel-Stil).
              Alle teuren Ableitungen (abGroups, accessIssues, rowItems,
              driveLetterCounts, …) sind oberhalb als `useMemo` definiert und
              rechnen nicht bei jedem Render neu. */}
          {totalIssues > 0 && (
            <PlausibilityBanner
              issues={accessIssues}
              folders={folders}
              allFolders={data.folders}
              shares={allShares}
              permissions={data.permissions}
              groups={abGroups}
              domainPrefix={data.domainPrefix}
              onApplyFixes={(suggestions) => {
                update({ permissions: applySuggestions(data.permissions, suggestions) })
              }}
            />
          )}
              <div
                className={cn(
                  'bg-white border border-gray-300 rounded-lg overflow-hidden flex flex-col',
                  isFullscreen
                    ? 'fixed inset-0 z-modal rounded-none border-0'
                    // Hoehe = Viewport − Platz fuer Topbar/PageHeader/Tabs/Filter +
                    // Body-Padding. Bei grossen Matrizen scrollt intern; bei kleinen
                    // schrumpft der Wrapper auf Inhalt, Legende sitzt direkt drunter.
                    : 'max-h-[calc(100vh-340px)] min-h-[360px]',
                )}
              >
                {/* Toolbar ueber der Matrix — enthaelt Vollbild-Toggle. Im Vollbild-
                    Modus ist das der einzige Weg zurueck (zusaetzlich ESC). */}
                <div className={cn(
                  'flex items-center justify-between px-2 py-1 border-b border-gray-200 bg-gray-50 text-caption text-gray-500 shrink-0',
                  !isFullscreen && 'rounded-t-lg',
                )}>
                  <span>
                    {isFullscreen ? 'Vollbild-Modus — ESC zum Schließen' : 'Matrix'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsFullscreen((v) => !v)}
                    className="flex items-center gap-1 px-2 py-0.5 rounded hover:bg-white hover:text-gray-700 border border-transparent hover:border-gray-300 transition"
                    title={isFullscreen ? 'Vollbild verlassen (ESC)' : 'Matrix im Vollbild bearbeiten'}
                    aria-label={isFullscreen ? 'Vollbild verlassen' : 'Vollbild aktivieren'}
                  >
                    {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">{isFullscreen ? 'Verlassen' : 'Vollbild'}</span>
                  </button>
                </div>
                <div className="overflow-auto flex-1 min-h-0">
                  <table className="w-full text-sm border-separate border-spacing-0 border border-gray-300">
                    <thead className="bg-gray-100 text-xs">
                      {/* Mega-Header: "Abteilungen" ueber den AB-Spalten */}
                      <tr className="align-bottom">
                        <th colSpan={6} className={cn('border border-gray-300 bg-gray-50 sticky top-0 z-40', STICKY.lw)} />
                        <th
                          colSpan={Math.max(1, abGroups.length)}
                          className="border border-gray-300 px-2 py-1 text-center align-bottom text-sm font-bold tracking-wide text-gray-700 bg-gray-200 sticky top-0 z-30"
                        >
                          Abteilungen
                        </th>
                      </tr>
                      <tr className="align-bottom">
                        <th className={cn('border border-gray-300 text-center px-1 py-2 font-semibold text-gray-700 bg-gray-100 w-[36px] min-w-[36px] sticky top-[29px] z-40', STICKY.lw)}>LW</th>
                        <th className={cn('border border-gray-300 text-left px-2 py-2 font-semibold text-gray-700 bg-gray-100 w-[72px] min-w-[72px] sticky top-[29px] z-40', STICKY.benennung)}>Benennung</th>
                        <th className={cn('border border-gray-300 text-left px-2 py-2 font-semibold text-gray-700 bg-gray-100 w-[70px] min-w-[70px] sticky top-[29px] z-40', STICKY.server)}>Server</th>
                        <th className={cn('border border-gray-300 text-left px-2 py-2 font-semibold text-gray-700 bg-gray-100 w-[70px] min-w-[70px] sticky top-[29px] z-40', STICKY.share)}>Share</th>
                        <th className={cn('border border-gray-300 text-left px-2 py-2 font-semibold text-gray-700 bg-gray-100 w-[260px] min-w-[260px] sticky top-[29px] z-40', STICKY.ordner)}>Ordner</th>
                        <th className={cn('border border-gray-300 text-left px-2 py-2 font-semibold text-gray-700 bg-gray-100 w-[110px] min-w-[110px] relative sticky top-[29px] z-40', STICKY.verantwortlich)}>
                          Verantwortlich
                          <button
                            type="button"
                            onClick={addAbteilung}
                            className="absolute top-1 -right-2 w-4 h-4 bg-white border border-brand-300 rounded-full flex items-center justify-center text-brand-500 hover:bg-brand-50 hover:border-brand-500 hover:text-brand-700 shadow-sm z-20 transition opacity-60 hover:opacity-100"
                            title="Neue Abteilung einfügen"
                            aria-label="Neue Abteilung einfügen"
                          >
                            <Plus className="w-2.5 h-2.5" />
                          </button>
                        </th>
                        {abGroups.length === 0 ? (
                          <th className="relative border border-gray-300 px-2 py-2 bg-gray-100 text-center text-[10px] text-gray-400 italic min-w-[80px] sticky top-[29px] z-30">
                            (noch keine)
                            <button
                              type="button"
                              onClick={addAbteilung}
                              className="absolute top-1 -right-2 w-4 h-4 bg-white border border-brand-300 rounded-full flex items-center justify-center text-brand-500 hover:bg-brand-50 hover:border-brand-500 hover:text-brand-700 shadow-sm z-20 transition"
                              title="Erste Abteilung hinzufügen"
                              aria-label="Erste Abteilung hinzufügen"
                            >
                              <Plus className="w-2.5 h-2.5" />
                            </button>
                          </th>
                        ) : (
                          abGroups.map((g) => (
                            <th
                              key={g.id}
                              className="relative border border-gray-300 px-0 pt-2 pb-6 font-medium text-gray-700 bg-gray-100 text-center align-bottom w-[28px] min-w-[28px] max-w-[32px] sticky top-[29px] z-30"
                            >
                              {/* Bulk-Apply-Button unten im Header */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (bulkMenu?.groupId === g.id) {
                                    setBulkMenu(null)
                                    return
                                  }
                                  const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect()
                                  // Popup oeffnet nach oben (wie vorher via `bottom-6`):
                                  // rechter Rand am Button-Rand, Boden knapp ueber dem Button.
                                  setBulkMenu({
                                    groupId: g.id,
                                    x: rect.right,
                                    y: rect.top - 8,
                                  })
                                }}
                                className="absolute bottom-1 right-1 p-0.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded z-10"
                                title="Preset auf alle Ordner dieser Gruppe anwenden"
                                aria-label="Preset auf alle Ordner dieser Gruppe anwenden"
                              >
                                <Sliders className="w-3.5 h-3.5" />
                              </button>
                              <div className="flex items-end justify-center" style={{ height: '180px', overflow: 'hidden' }}>
                                <div
                                  className="text-[11px] whitespace-nowrap"
                                  style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                                  title={fullGroupName(data.domainPrefix, g.type, g.name)}
                                >
                                  {g.teamName || fullGroupName(data.domainPrefix, g.type, g.name)}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={addAbteilung}
                                className="absolute top-1 -right-2 w-4 h-4 bg-white border border-brand-300 rounded-full flex items-center justify-center text-brand-500 hover:bg-brand-50 hover:border-brand-500 hover:text-brand-700 shadow-sm z-20 transition opacity-60 hover:opacity-100"
                                title="Neue Abteilung rechts einfügen"
                                aria-label="Neue Abteilung rechts einfügen"
                              >
                                <Plus className="w-2.5 h-2.5" />
                              </button>
                            </th>
                          ))
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Share-Root-Zeilen + Folder-Baum (parents -> children) */}
                      {rowItems.map((row, idx) => {
                        // ── Share-Root-Zeile ─────────────────────────────────
                        if (row.kind === 'share') {
                          const rowShareId = row.shareId
                          const srv = getServerOfShare(rowShareId)
                          const shareObj = srv?.shares.find((sh) => sh.id === rowShareId)
                          // Nur der Share-Root-Drive (subPath leer). Drives die an
                          // Unterordner gemappt sind werden in den Folder-Zeilen angezeigt.
                          const firstDrive = getShareRootDrive(rowShareId)
                          const shareDraftKey = `share:${rowShareId}`
                          const shareFolderCount = data.folders.filter(
                              (f) => f.shareId === rowShareId,
                            ).length
                          const shareDriveCount = data.drives.filter(
                              (d) => d.shareId === rowShareId,
                            ).length
                          const requestDeleteShare = () => {
                            const shareLabel = shareObj?.name
                              ? `\u00BB${shareObj.name}\u00AB`
                              : 'dieser Share'
                            setConfirmDialog({
                              title: 'Share löschen',
                              confirmLabel: 'Share löschen',
                              message: (
                                <div className="space-y-2">
                                  <p>
                                    Soll der Share {shareLabel} wirklich komplett gelöscht werden?
                                  </p>
                                  <ul className="text-xs text-gray-600 list-disc list-inside space-y-0.5">
                                    <li>{shareFolderCount} Ordner (inkl. Unterordner)</li>
                                    <li>{shareDriveCount} Laufwerks-Mapping(s)</li>
                                    <li>Alle Berechtigungen für diesen Share</li>
                                  </ul>
                                  <p className="text-xs text-gray-500">
                                    Nach dem Speichern kann dieser Stand aus der Versions-Historie wiederhergestellt werden.
                                  </p>
                                </div>
                              ),
                              onConfirm: () => {
                                deleteShare(rowShareId)
                                setConfirmDialog(null)
                              },
                            })
                          }
                          const shareStickyBg = 'bg-gray-50 group-hover:bg-gray-100'
                          return (
                            <Fragment key={`share-${row.shareId}`}>
                              <tr className={cn('group relative bg-gray-50', idx > 0 && 'border-t-2 border-t-gray-300')}>
                                <td className={cn('border border-gray-300 px-0.5 py-1 text-caption font-semibold text-gray-700 z-10', STICKY.lw, shareStickyBg)}>
                                  <div className="flex items-center justify-center gap-0.5">
                                    {caps.canEditDriveMetadata ? (
                                      <input
                                        type="text"
                                        maxLength={1}
                                        value={firstDrive?.letter ?? ''}
                                        onChange={(e) => {
                                          if (firstDrive) {
                                            updateDrive(firstDrive.id, { letter: e.target.value })
                                          } else if (e.target.value) {
                                            createDriveForShare(rowShareId, { letter: e.target.value })
                                          }
                                        }}
                                        placeholder="?"
                                        title={
                                          isDuplicateLetter(firstDrive?.letter)
                                            ? `Laufwerksbuchstabe "${(firstDrive?.letter || '').toUpperCase()}:" ist mehrfach vergeben!`
                                            : undefined
                                        }
                                        className={cn(
                                          'w-7 px-0.5 py-0.5 text-caption font-mono font-bold text-center border rounded focus:outline-none focus:ring-1 focus:ring-brand-500',
                                          isDuplicateLetter(firstDrive?.letter)
                                            ? 'border-red-300 bg-red-50 text-red-700'
                                            : 'border-transparent hover:border-gray-300 focus:border-brand-500',
                                        )}
                                      />
                                    ) : (
                                      <span className="w-7 px-0.5 py-0.5 text-caption font-mono font-bold text-center">
                                        {firstDrive?.letter ?? '–'}
                                      </span>
                                    )}
                                    <span className="text-gray-400 font-mono">:</span>
                                  </div>
                                </td>
                                <td className={cn('border border-gray-300 px-2 py-1 text-caption text-gray-700 z-10', STICKY.benennung, shareStickyBg)}>
                                  {caps.canEditDriveMetadata ? (
                                    <input
                                      type="text"
                                      value={firstDrive?.label ?? ''}
                                      onChange={(e) => {
                                        if (firstDrive) {
                                          updateDrive(firstDrive.id, { label: e.target.value })
                                        } else if (e.target.value) {
                                          createDriveForShare(row.shareId, { label: e.target.value })
                                        }
                                      }}
                                      placeholder="Benennung"
                                      className="w-full px-1 py-0.5 text-caption border border-transparent hover:border-gray-300 focus:border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                                    />
                                  ) : (
                                    <span className="px-1 py-0.5 text-caption">
                                      {firstDrive?.label ?? ''}
                                    </span>
                                  )}
                                </td>
                                <td className={cn('border border-gray-300 px-2 py-1 text-caption text-gray-700 z-10', STICKY.server, shareStickyBg)}>
                                  {srv?.name || ''}
                                </td>
                                <td className={cn('border border-gray-300 px-2 py-1 text-caption text-gray-700 z-10', STICKY.share, shareStickyBg)}>
                                  {shareObj?.name || ''}
                                </td>
                                <td className={cn('border border-gray-300 px-2 py-1 z-10', STICKY.ordner, shareStickyBg)}>
                                  <div className="flex items-center gap-1 text-caption text-gray-500 italic">
                                    <FolderIcon className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                                    <span className="font-medium flex-1 min-w-0">Share-Root</span>
                                    <button
                                      type="button"
                                      onClick={() => openSubDraft(shareDraftKey)}
                                      className="p-0.5 text-gray-300 hover:text-brand-600 rounded opacity-0 group-hover:opacity-100 transition"
                                      title="Ordner im Share anlegen"
                                      aria-label="Ordner im Share anlegen"
                                    >
                                      <FolderPlus className="w-3.5 h-3.5" />
                                    </button>
                                    {caps.canEditInfrastructure && (
                                      <button
                                        type="button"
                                        onClick={requestDeleteShare}
                                        className="p-0.5 text-gray-300 hover:text-red-500 rounded"
                                        title="Kompletten Share löschen"
                                        aria-label="Kompletten Share löschen"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td className={cn('border border-gray-300 px-1 py-0.5 text-caption text-gray-700 z-10', STICKY.verantwortlich, shareStickyBg)}>
                                  <ResponsiblePicker
                                    value={shareObj?.responsibleEmployees ?? []}
                                    onChange={(next) => updateShare(row.shareId, { responsibleEmployees: next.length > 0 ? next : undefined })}
                                    inherited={null}
                                  />
                                </td>
                                {abGroups.length === 0 ? (
                                  <td className="border border-gray-300 bg-gray-50" />
                                ) : (
                                  abGroups.map((g) => {
                                    const target: PermTarget = { shareId: row.shareId }
                                    // Share-Root hat keine Vererbung von „oben" (Top der Kette),
                                    // also explizit `inherited={null}`. Plausibilitaets-Issues
                                    // werden nur fuer Ordner aggregiert, Shares bekommen keine.
                                    return (
                                      <PermissionCell
                                        key={g.id}
                                        target={target}
                                        groupId={g.id}
                                        direct={getPermission(target, g.id)}
                                        inherited={null}
                                        issue={null}
                                        advancedMode={advancedMode}
                                        onCycle={onCellCycle}
                                        onOpenMenu={onCellOpenMenu}
                                      />
                                    )
                                  })
                                )}
                              </tr>
                              {subDrafts[shareDraftKey] !== undefined && (
                                <tr key={`sub-${shareDraftKey}`} className="bg-brand-50">
                                  <td className={cn('border border-gray-300 px-1 py-1 text-caption text-gray-400 text-center z-10 bg-brand-50', STICKY.lw)} />
                                  <td className={cn('border border-gray-300 px-2 py-1 z-10 bg-brand-50', STICKY.benennung)} />
                                  <td className={cn('border border-gray-300 px-2 py-1 z-10 bg-brand-50', STICKY.server)} />
                                  <td className={cn('border border-gray-300 px-2 py-1 z-10 bg-brand-50', STICKY.share)} />
                                  <td className={cn('border border-gray-300 px-2 py-1 z-10 bg-brand-50', STICKY.ordner)}>
                                    <div className="flex items-stretch">
                                      <span aria-hidden className="flex-shrink-0 border-l border-gray-200" style={{ width: '16px', marginLeft: '2px' }} />
                                      <div className="flex items-center gap-1 flex-1 min-w-0">
                                        <FolderIcon className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                                        <input
                                          autoFocus
                                          type="text"
                                          value={subDrafts[shareDraftKey]}
                                          onChange={(e) => updateSubDraft(shareDraftKey, e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') { e.preventDefault(); commitSubDraft(shareDraftKey) }
                                            if (e.key === 'Escape') { e.preventDefault(); closeSubDraft(shareDraftKey) }
                                          }}
                                          placeholder="Ordnername"
                                          className="flex-1 min-w-0 px-1 py-0.5 text-caption border border-brand-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => closeSubDraft(shareDraftKey)}
                                          className="p-0.5 text-gray-400 hover:text-red-500 rounded"
                                          title="Abbrechen"
                                          aria-label="Abbrechen"
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                  <td className={cn('border border-gray-300 z-10 bg-brand-50', STICKY.verantwortlich)} />
                                  {abGroups.length === 0 ? (
                                    <td className="border border-gray-300 bg-gray-50" />
                                  ) : (
                                    abGroups.map((g) => (
                                      <td key={g.id} className="border border-gray-300 bg-gray-50" />
                                    ))
                                  )}
                                </tr>
                              )}
                            </Fragment>
                          )
                        }

                        // ── Folder-Zeile ─────────────────────────────────────
                        const f = row.folder
                        const depth = folderDepth(f)
                        const hasChildren = folders.some((c) => c.parent === f.id)
                        const inheritedResponsibles = getInheritedResponsibles(f, data.folders, allShares)
                        // Eigener Drive des Ordners (subPath === relativePath).
                        // Nur hier zeigen wir LW + Benennung — NICHT den geerbten
                        // Drive eines Parent, damit der User sieht ob dieser
                        // Ordner ein eigenes Mapping hat oder nicht.
                        const ownDrive = getFolderOwnDrive(f)
                        const folderDescCount = data.folders.filter(
                            (x) => x.shareId === f.shareId && x.relativePath.startsWith(`${f.relativePath}\\`),
                          ).length
                        function requestDeleteFolder() {
                          const folderLabel = `\u00BB${f.name}\u00AB`
                          const bulletLines: string[] = []
                          if (folderDescCount > 0) {
                            bulletLines.push(
                              `${folderDescCount} Unterordner werden mitgelöscht`,
                            )
                          }
                          if (ownDrive && ownDrive.letter) {
                            bulletLines.push(
                              `Laufwerks-Mapping ${ownDrive.letter}: wird entfernt`,
                            )
                          }
                          bulletLines.push('Alle Berechtigungen für diesen Ordner')
                          setConfirmDialog({
                            title: 'Ordner löschen',
                            confirmLabel: 'Ordner löschen',
                            message: (
                              <div className="space-y-2">
                                <p>
                                  Soll der Ordner {folderLabel} wirklich gelöscht werden?
                                </p>
                                <ul className="text-xs text-gray-600 list-disc list-inside space-y-0.5">
                                  {bulletLines.map((line, i) => (
                                    <li key={i}>{line}</li>
                                  ))}
                                </ul>
                                <p className="text-xs text-gray-500">
                                  Nach dem Speichern kann dieser Stand aus der Versions-Historie wiederhergestellt werden.
                                </p>
                              </div>
                            ),
                            onConfirm: () => {
                              deleteFolder(f.id)
                              setConfirmDialog(null)
                            },
                          })
                        }
                        const folderStickyBg = 'bg-white group-hover:bg-gray-50'
                        return (
                          <Fragment key={f.id}>
                          <tr className="hover:bg-gray-50 group relative">
                            <td className={cn('border border-gray-300 px-0.5 py-1 text-caption font-semibold text-gray-700 z-10', STICKY.lw, folderStickyBg)}>
                              <div className="flex items-center justify-center gap-0.5">
                                <input
                                  type="text"
                                  maxLength={1}
                                  value={ownDrive?.letter ?? ''}
                                  onChange={(e) => setFolderDrivePatch(f, { letter: e.target.value })}
                                  placeholder="?"
                                  title={
                                    isDuplicateLetter(ownDrive?.letter)
                                      ? `Laufwerksbuchstabe "${(ownDrive?.letter || '').toUpperCase()}:" ist mehrfach vergeben!`
                                      : 'Laufwerksbuchstabe für diesen Unterordner (GPO-Mapping auf den Ordner-Pfad)'
                                  }
                                  className={cn(
                                    'w-7 px-0.5 py-0.5 text-caption font-mono font-bold text-center border rounded focus:outline-none focus:ring-1 focus:ring-brand-500',
                                    isDuplicateLetter(ownDrive?.letter)
                                      ? 'border-red-300 bg-red-50 text-red-700'
                                      : 'border-transparent hover:border-gray-300 focus:border-brand-500',
                                  )}
                                />
                                <span className="text-gray-400 font-mono">{ownDrive?.letter ? ':' : ''}</span>
                              </div>
                            </td>
                            <td className={cn('border border-gray-300 px-2 py-1 text-caption text-gray-700 z-10', STICKY.benennung, folderStickyBg)}>
                              <input
                                type="text"
                                value={ownDrive?.label ?? ''}
                                onChange={(e) => setFolderDrivePatch(f, { label: e.target.value })}
                                placeholder="Benennung"
                                title="Anzeigename des Laufwerks (nur wenn dieser Ordner ein eigenes Mapping hat)"
                                className="w-full px-1 py-0.5 text-caption border border-transparent hover:border-gray-300 focus:border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                              />
                            </td>
                            <td className={cn('border border-gray-300 px-2 py-1 text-caption text-gray-700 z-10', STICKY.server, folderStickyBg)} />
                            <td className={cn('border border-gray-300 px-2 py-1 text-caption text-gray-700 z-10', STICKY.share, folderStickyBg)} />
                            <td className={cn('border border-gray-300 px-2 py-1 z-10', STICKY.ordner, folderStickyBg)}>
                              <div className="flex items-stretch">
                                {/* Baum-Guide: Share-Root + pro Tiefenstufe ein 16px-Slot mit linker Linie */}
                                {Array.from({ length: depth + 1 }).map((_, i) => (
                                  <span
                                    key={i}
                                    aria-hidden
                                    className="flex-shrink-0 border-l border-gray-200"
                                    style={{ width: '16px', marginLeft: i === 0 ? '2px' : 0 }}
                                  />
                                ))}
                                <div className="flex items-center gap-1 flex-1 min-w-0">
                                  <FolderIcon className={cn('w-3.5 h-3.5 flex-shrink-0', hasChildren ? 'text-amber-600' : 'text-amber-500')} />
                                  <input
                                    type="text"
                                    value={f.name}
                                    onChange={(e) => updateFolder(f.id, { name: e.target.value })}
                                    className="flex-1 min-w-0 px-1 py-0.5 text-caption border border-transparent hover:border-gray-300 focus:border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => openSubDraft(f.id)}
                                    className="p-0.5 text-gray-300 hover:text-brand-600 rounded opacity-0 group-hover:opacity-100 transition"
                                    title="Unterordner anlegen"
                                    aria-label="Unterordner anlegen"
                                  >
                                    <FolderPlus className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={requestDeleteFolder}
                                    className="p-0.5 text-gray-300 hover:text-red-500 rounded"
                                    title="Ordner löschen"
                                    aria-label="Ordner löschen"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td className={cn('border border-gray-300 px-1 py-0.5 text-caption text-gray-700 z-10', STICKY.verantwortlich, folderStickyBg)}>
                              <ResponsiblePicker
                                value={f.responsibleEmployees ?? []}
                                onChange={(next) => updateFolder(f.id, { responsibleEmployees: next.length > 0 ? next : undefined })}
                                inherited={inheritedResponsibles}
                              />
                            </td>
                            {abGroups.length === 0 ? (
                              <td className="border border-gray-300 bg-gray-50" />
                            ) : (
                              abGroups.map((g) => {
                                const target: PermTarget = { folderId: f.id }
                                const direct = getPermission(target, g.id)
                                const inherited = direct ? null : getInheritedRights(f.id, g.id)
                                const issue = accessIssues.get(f.id)?.get(g.id) ?? null
                                return (
                                  <PermissionCell
                                    key={g.id}
                                    target={target}
                                    groupId={g.id}
                                    direct={direct}
                                    inherited={inherited}
                                    issue={issue}
                                    advancedMode={advancedMode}
                                    onCycle={onCellCycle}
                                    onOpenMenu={onCellOpenMenu}
                                  />
                                )
                              })
                            )}
                          </tr>
                          {subDrafts[f.id] !== undefined && (
                            <tr key={`sub-${f.id}`} className="bg-brand-50">
                              <td className={cn('border border-gray-300 px-1 py-1 text-caption text-gray-400 text-center z-10 bg-brand-50', STICKY.lw)} />
                              <td className={cn('border border-gray-300 px-2 py-1 z-10 bg-brand-50', STICKY.benennung)} />
                              <td className={cn('border border-gray-300 px-2 py-1 z-10 bg-brand-50', STICKY.server)} />
                              <td className={cn('border border-gray-300 px-2 py-1 z-10 bg-brand-50', STICKY.share)} />
                              <td className={cn('border border-gray-300 px-2 py-1 z-10 bg-brand-50', STICKY.ordner)}>
                                <div className="flex items-stretch">
                                  {Array.from({ length: depth + 1 }).map((_, i) => (
                                    <span
                                      key={i}
                                      aria-hidden
                                      className="flex-shrink-0 border-l border-gray-200"
                                      style={{ width: '16px', marginLeft: i === 0 ? '2px' : 0 }}
                                    />
                                  ))}
                                  <div className="flex items-center gap-1 flex-1 min-w-0">
                                    <FolderIcon className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                                    <input
                                      autoFocus
                                      type="text"
                                      value={subDrafts[f.id]}
                                      onChange={(e) => updateSubDraft(f.id, e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') { e.preventDefault(); commitSubDraft(f.id) }
                                        if (e.key === 'Escape') { e.preventDefault(); closeSubDraft(f.id) }
                                      }}
                                      placeholder="Unterordnername"
                                      className="flex-1 min-w-0 px-1 py-0.5 text-caption border border-brand-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => closeSubDraft(f.id)}
                                      className="p-0.5 text-gray-400 hover:text-red-500 rounded"
                                      title="Abbrechen"
                                      aria-label="Abbrechen"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>
                              </td>
                              <td className={cn('border border-gray-300 z-10 bg-brand-50', STICKY.verantwortlich)} />
                              {abGroups.length === 0 ? (
                                <td className="border border-gray-300 bg-gray-50" />
                              ) : (
                                abGroups.map((g) => (
                                  <td key={g.id} className="border border-gray-300 bg-gray-50" />
                                ))
                              )}
                            </tr>
                          )}
                          </Fragment>
                        )
                      })}
                      {/* Draft-Zeilen fuer Direkt-Eingabe (min. 10) —
                          nur wenn der Nutzer Infrastruktur anlegen darf. */}
                      {caps.canEditInfrastructure && draftRows.map((row, rowIdx) => {
                        const anyFilled =
                          row.driveLetter ||
                          row.driveLabel ||
                          row.serverName ||
                          row.shareName ||
                          row.levelNames.some((v) => v && v.trim()) ||
                          row.responsibleEmployees.length > 0
                        const serverSuggestions = data.servers.map((s) => s.name)
                        const shareSuggestions = data.servers.flatMap((s) => s.shares.map((sh) => sh.name))
                        const draftStickyBg = anyFilled ? 'bg-brand-50' : 'bg-white group-hover:bg-gray-50'
                        return (
                          <tr key={`draft-${rowIdx}`} className={cn('group', anyFilled ? 'bg-brand-50' : 'hover:bg-gray-50')}>
                            <td className={cn('border border-gray-300 px-1 py-0.5 z-10', STICKY.lw, draftStickyBg)}>
                              <input
                                type="text"
                                value={row.driveLetter}
                                onChange={(e) =>
                                  updateDraftRow(rowIdx, {
                                    driveLetter: e.target.value
                                      .replace(/:$/, '')
                                      .toUpperCase()
                                      .slice(0, 1),
                                  })
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault()
                                    commitDraftRow(rowIdx)
                                  }
                                }}
                                placeholder="E"
                                maxLength={2}
                                className="w-full px-1 py-0.5 text-small font-bold text-center border border-transparent hover:border-gray-300 focus:border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 bg-transparent"
                              />
                            </td>
                            <td className={cn('border border-gray-300 px-1 py-0.5 z-10', STICKY.benennung, draftStickyBg)}>
                              <input
                                type="text"
                                value={row.driveLabel}
                                onChange={(e) => updateDraftRow(rowIdx, { driveLabel: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault()
                                    commitDraftRow(rowIdx)
                                  }
                                }}
                                placeholder="Benennung"
                                className="w-full px-1 py-0.5 text-caption border border-transparent hover:border-gray-300 focus:border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 bg-transparent"
                              />
                            </td>
                            <td className={cn('border border-gray-300 px-1 py-0.5 z-10', STICKY.server, draftStickyBg)}>
                              <input
                                type="text"
                                value={row.serverName}
                                list={`server-suggestions-${rowIdx}`}
                                onChange={(e) => updateDraftRow(rowIdx, { serverName: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault()
                                    commitDraftRow(rowIdx)
                                  }
                                }}
                                placeholder={primaryServer?.name ?? 'Server'}
                                className="w-full px-1 py-0.5 text-caption border border-transparent hover:border-gray-300 focus:border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 bg-transparent"
                              />
                              <datalist id={`server-suggestions-${rowIdx}`}>
                                {serverSuggestions.map((n) => (
                                  <option key={n} value={n} />
                                ))}
                              </datalist>
                            </td>
                            <td className={cn('border border-gray-300 px-1 py-0.5 z-10', STICKY.share, draftStickyBg)}>
                              <input
                                type="text"
                                value={row.shareName}
                                list={`share-suggestions-${rowIdx}`}
                                onChange={(e) => updateDraftRow(rowIdx, { shareName: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault()
                                    commitDraftRow(rowIdx)
                                  }
                                }}
                                placeholder="Share"
                                className="w-full px-1 py-0.5 text-caption border border-transparent hover:border-gray-300 focus:border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 bg-transparent"
                              />
                              <datalist id={`share-suggestions-${rowIdx}`}>
                                {[...new Set(shareSuggestions)].map((n) => (
                                  <option key={n} value={n} />
                                ))}
                              </datalist>
                            </td>
                            <td className={cn('border border-gray-300 px-1 py-0.5 z-10', STICKY.ordner, draftStickyBg)}>
                              <input
                                type="text"
                                value={row.levelNames.filter((v) => v != null && v !== '').join('/')}
                                onChange={(e) => updateDraftRow(rowIdx, { levelNames: e.target.value.split('/').map((p) => p.trim()) })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault()
                                    commitDraftRow(rowIdx)
                                  }
                                }}
                                placeholder="Ordner/Unterordner"
                                className="w-full px-1 py-0.5 text-caption border border-transparent hover:border-gray-300 focus:border-brand-500 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 bg-transparent"
                              />
                            </td>
                            <td className={cn('border border-gray-300 px-1 py-0.5 z-10', STICKY.verantwortlich, draftStickyBg)}>
                              <ResponsiblePicker
                                value={row.responsibleEmployees}
                                onChange={(next) => updateDraftRow(rowIdx, { responsibleEmployees: next })}
                                inherited={null}
                              />
                            </td>
                            {abGroups.length === 0 ? (
                              <td className="border border-gray-300 bg-white" />
                            ) : (
                              abGroups.map((g) => (
                                <td key={g.id} className="border border-gray-300 bg-white" />
                              ))
                            )}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                {otherGroups.length > 0 && (
                  <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 text-caption text-gray-500 shrink-0">
                    Nicht-Abteilungs-Gruppen ({otherGroups.length}) werden in der Matrix ausgeblendet —
                    sie bleiben für LW/FU/SW/LI/PR-Zuweisungen in anderen Tabs erhalten:{' '}
                    {otherGroups.map((g) => fullGroupName(data.domainPrefix, g.type, g.name)).join(', ')}
                  </div>
                )}
                {/* Kompakte Legende — immer sichtbar, auch im Vollbild. Alle Badges
                    in einer wrap-flex-Zeile, Advanced-Toggle rechts. */}
                <div className="shrink-0 border-t border-gray-200 bg-gray-50 px-3 py-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-gray-600">
                  <span className="font-medium text-gray-500">Legende:</span>
                  {advancedMode ? (
                    <>
                      <span className="flex items-center gap-1.5">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-white border border-gray-300 text-gray-500 min-w-[2rem] text-center">—</span>
                        <span>(vererben)</span>
                      </span>
                      {RIGHTS_OPTIONS.map((o) => (
                        <span key={o.value} className="flex items-center gap-1.5">
                          <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-mono font-bold border min-w-[2rem] text-center text-gray-900', RIGHTS_BADGE[o.value])}>{o.short}</span>
                          <span>{o.label}</span>
                        </span>
                      ))}
                    </>
                  ) : (
                    PERMISSION_PRESETS.filter((p) => p.value !== 'full').map((p) => (
                      <span key={p.value} className="flex items-center gap-1.5" title={p.description}>
                        <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-mono font-bold border min-w-[2.5rem] text-center text-gray-900', PRESET_BADGE[p.category])}>{p.short}</span>
                        <span>{p.label}</span>
                      </span>
                    ))
                  )}
                  <span className="text-gray-400 hidden md:inline">·</span>
                  <span className="text-gray-500 hidden md:inline">Blasse Farbe = geerbt</span>
                  <label
                    className="ml-auto flex items-center gap-1.5 cursor-pointer select-none whitespace-nowrap"
                    title="Zeigt die vollen 7 NTFS-Rechte-Stufen statt der 6 Presets. Für Spezialfälle."
                  >
                    <Wrench className="w-3.5 h-3.5 text-gray-400" />
                    <Checkbox
                      checked={advancedMode}
                      onChange={(e) => setAdvancedMode(e.target.checked)}
                    />
                    <span>Erweitert</span>
                  </label>
                </div>
              </div>
        </>
      )}

      {/* Excel Import Preview Modal */}
      <Modal
        open={showImportModal}
        onOpenChange={(v) => !v && closeImportModal()}
        size="md"
        title={
          <span className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-brand-500" />
            Excel-Import {importPreview ? '– Vorschau' : ''}
          </span>
        }
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={closeImportModal}>
              Abbrechen
            </Button>
            {importPreview && !importError && (
              <Button variant="primary" size="sm" onClick={confirmImport}>
                Übernehmen
              </Button>
            )}
          </>
        }
      >
        <div className="space-y-4 text-body">
          {importFile && (
            <div className="text-caption text-gray-500">
              Datei: <span className="font-mono text-gray-700">{importFile.name}</span>
            </div>
          )}
          {importError ? (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">Import fehlgeschlagen</div>
                <div className="text-caption mt-1">{importError}</div>
              </div>
            </div>
          ) : importPreview ? (
            <>
              <label className="flex items-center gap-2 text-caption cursor-pointer select-none">
                <Checkbox
                  checked={importReplaceExisting}
                  onChange={(e) => toggleReplaceExisting(e.target.checked)}
                />
                <span className="font-medium text-gray-700">Bestehende Ordner & Berechtigungen ersetzen</span>
                <span className="text-gray-400">(empfohlen bei Neu-Import)</span>
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded p-3 space-y-1 text-gray-700">
                <div className="flex justify-between"><span>Zeilen gelesen:</span><b>{importPreview.summary.rowsImported}</b></div>
                <div className="flex justify-between"><span>Neue Ordner:</span><b>{importPreview.summary.foldersCreated}</b></div>
                <div className="flex justify-between"><span>Aktualisierte Ordner:</span><b>{importPreview.summary.foldersUpdated}</b></div>
                <div className="flex justify-between"><span>Neue Laufwerke:</span><b>{importPreview.summary.drivesCreated}</b></div>
                <div className="flex justify-between"><span>Neue Gruppen (AB):</span><b>{importPreview.summary.groupsCreated}</b></div>
                <div className="flex justify-between"><span>Berechtigungen gesetzt:</span><b>{importPreview.summary.permissionsSet}</b></div>
              </div>
              {importPreview.summary.warnings.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-800 space-y-1">
                  <div className="font-medium flex items-center gap-1 text-caption">
                    <AlertTriangle className="w-3.5 h-3.5" /> Hinweise:
                  </div>
                  <ul className="list-disc list-inside text-caption space-y-0.5">
                    {importPreview.summary.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(() => {
                const debug = importPreview.parsed.debug
                if (!debug) return null
                const colNames = Object.entries(debug.columnsFound)
                  .filter(([, idx]) => idx >= 0)
                  .map(([name, idx]) => `${name} (Sp.${idx + 1})`)
                const missingCols = Object.entries(debug.columnsFound)
                  .filter(([, idx]) => idx === -1)
                  .map(([name]) => name)
                return (
                  <div className="border border-gray-200 rounded text-caption">
                    <button
                      type="button"
                      onClick={() => setShowImportDebug((v) => !v)}
                      className="w-full flex items-center gap-1.5 px-3 py-2 text-gray-500 hover:bg-gray-50 font-medium"
                    >
                      {showImportDebug ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      Diagnose (Header-Zeile {debug.headerRowIdx + 1}, {colNames.length} Spalten erkannt)
                    </button>
                    {showImportDebug && (
                      <div className="px-3 pb-3 space-y-2 border-t border-gray-100">
                        <div className="mt-2">
                          <div className="text-gray-400 mb-1">Erkannte Spalten:</div>
                          <div className="flex flex-wrap gap-1">
                            {colNames.map((c) => (
                              <span key={c} className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded font-mono">{c}</span>
                            ))}
                            {missingCols.map((c) => (
                              <span key={c} className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-mono">{c} (fehlt)</span>
                            ))}
                          </div>
                        </div>
                        {debug.rawHeaderCells.some(Boolean) && (
                          <div>
                            <div className="text-gray-400 mb-1">Header-Zeile (Sp. 1–{debug.rawHeaderCells.length}):</div>
                            <div className="flex flex-wrap gap-1">
                              {debug.rawHeaderCells.map((c, i) => (
                                <span key={i} className={`px-1.5 py-0.5 rounded font-mono ${c ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-300'}`}>
                                  {i + 1}: {c || '∅'}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {debug.rawFirstDataCells.some(Boolean) && (
                          <div>
                            <div className="text-gray-400 mb-1">Erste Datenzeile (Zeile {debug.headerRowIdx + 2}):</div>
                            <div className="flex flex-wrap gap-1">
                              {debug.rawFirstDataCells.map((c, i) => (
                                <span key={i} className={`px-1.5 py-0.5 rounded font-mono ${c ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-300'}`}>
                                  {i + 1}: {c || '∅'}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {debug.sampleRows.length > 0 && (
                          <div>
                            <div className="text-gray-400 mb-1">Erste {debug.sampleRows.length} geparste Zeilen:</div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-[10px] border-collapse">
                                <thead>
                                  <tr className="bg-gray-50">
                                    {['LW', 'Server', 'Share', 'Ebene 1', 'Ebene 2'].map((h) => (
                                      <th key={h} className="border border-gray-200 px-1.5 py-1 text-left font-medium text-gray-600">{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {debug.sampleRows.map((r, i) => (
                                    <tr key={i} className="hover:bg-gray-50">
                                      <td className="border border-gray-200 px-1.5 py-1 font-mono">{r.driveLetter || '–'}</td>
                                      <td className="border border-gray-200 px-1.5 py-1">{r.serverName || <span className="text-red-400 italic">leer</span>}</td>
                                      <td className="border border-gray-200 px-1.5 py-1">{r.shareName || <span className="text-red-400 italic">leer</span>}</td>
                                      <td className="border border-gray-200 px-1.5 py-1">{r.level1 || '–'}</td>
                                      <td className="border border-gray-200 px-1.5 py-1">{r.level2 || '–'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })()}
              <p className="text-caption text-gray-500">
                Beim Übernehmen werden die Änderungen in die lokale Bearbeitung geschrieben. Erst mit
                "Speichern" oben rechts wird eine neue Version abgelegt.
              </p>
            </>
          ) : (
            <div className="text-gray-500 text-small">Datei wird gelesen …</div>
          )}
        </div>
      </Modal>

      {/* Rechtsklick-Menue fuer Permission-Zellen. Via Portal + fixed-Position,
          damit es aus der Tabelle heraus nach oben „floaten" kann. */}
      {cellMenu &&
        createPortal(
          (() => {
            const menuTarget: PermTarget =
              cellMenu.folderId !== undefined
                ? { folderId: cellMenu.folderId }
                : { shareId: cellMenu.shareId! }
            return (
          <div
            ref={cellMenuRef}
            className="fixed z-popover bg-white border border-gray-200 shadow-popover rounded-md py-1 min-w-[220px] max-h-[calc(100vh-16px)] overflow-y-auto text-left"
            style={{ left: cellMenu.x, top: cellMenu.y }}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
          >
            <div className="px-3 py-1.5 text-caption uppercase tracking-wide text-gray-400 border-b border-gray-100">
              Berechtigung setzen
            </div>
            <button
              type="button"
              onClick={() => {
                setPermission(menuTarget, cellMenu.groupId, null)
                setCellMenu(null)
              }}
              className="w-full px-3 py-1.5 text-caption hover:bg-gray-50 flex items-center gap-2 text-gray-700"
              title="Erbt vom Parent-Ordner"
            >
              <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-white border border-gray-300 text-gray-500 min-w-[2.5rem] text-center">—</span>
              <span>(vererben)</span>
            </button>
            {advancedMode
              ? RIGHTS_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => {
                      setPermission(menuTarget, cellMenu.groupId, o.value)
                      setCellMenu(null)
                    }}
                    className="w-full px-3 py-1.5 text-caption hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                  >
                    <span
                      className={cn('inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-bold border min-w-[2.5rem] text-center text-gray-900', RIGHTS_BADGE[o.value])}
                    >
                      {o.short}
                    </span>
                    <span>{o.label}</span>
                  </button>
                ))
              : PERMISSION_PRESETS.filter((p) => p.value !== 'inherit').map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => {
                      setPermission(menuTarget, cellMenu.groupId, p.rights)
                      setCellMenu(null)
                    }}
                    className="w-full px-3 py-1.5 text-caption hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                    title={p.description}
                  >
                    <span
                      className={cn('inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-bold border min-w-[2.5rem] text-center text-gray-900', PRESET_BADGE[p.category])}
                    >
                      {p.short}
                    </span>
                    <span>{p.label}</span>
                  </button>
                ))}
          </div>
            )
          })(),
           
          document.body,
        )}

      {/* Bulk-Apply-Menue fuer eine Gruppen-Spalte. Via Portal + fixed-Position,
          damit es aus dem `overflow-x-auto`-Container der Tabelle raus kann. */}
      {bulkMenu &&
        createPortal(
          <div
            ref={bulkMenuRef}
            className="fixed z-popover bg-white border border-gray-200 shadow-popover rounded-md py-1 min-w-[220px] max-h-[calc(100vh-16px)] overflow-y-auto text-left"
            style={{
              left: bulkMenu.x,
              top: bulkMenu.y,
              transform: 'translate(-100%, -100%)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-1.5 text-caption uppercase tracking-wide text-gray-400 border-b border-gray-100">
              Preset auf alle Ordner
            </div>
            {PERMISSION_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => bulkApplyPresetToGroup(bulkMenu.groupId, p.value)}
                className="w-full px-3 py-1.5 text-caption hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                title={p.description}
              >
                <span
                  className={cn(
                    'inline-block w-4 h-4 rounded border border-gray-300 flex-shrink-0',
                    p.category === 'orange' && 'bg-orange-400',
                    p.category === 'green' && 'bg-green-300',
                    p.category === 'mixed' && 'bg-amber-200',
                    p.category === 'yellow' && 'bg-yellow-300',
                    p.category === 'red' && 'bg-red-200',
                    p.category === 'white' && 'bg-white',
                    p.category === 'gray' && 'bg-gray-200',
                  )}
                />
                <span className="font-mono text-caption text-gray-500 w-10 flex-shrink-0">{p.short}</span>
                <span>{p.label}</span>
              </button>
            ))}
          </div>,
           
          document.body,
        )}

      <ConfirmDialog
        open={confirmDialog !== null}
        title={confirmDialog?.title ?? ''}
        message={confirmDialog?.message ?? ''}
        confirmLabel={confirmDialog?.confirmLabel}
        variant="danger"
        onConfirm={() => confirmDialog?.onConfirm()}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  )
}
